import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import api from '../config/api';
import { toast } from 'react-toastify';
import { FaSearch, FaChevronDown, FaChevronUp, FaFileAlt, FaCalendarAlt, FaChartBar, FaComments, FaCheck, FaHome, FaCalendarCheck, FaCalendarTimes, FaSignature, FaSort, FaSortUp, FaSortDown, FaTimes, FaEye, FaEyeSlash, FaExpand, FaCompress, FaFileCsv, FaFileExcel } from 'react-icons/fa';
import {
  QualiteConfirmationAuditButton,
  QualiteConfirmationAuditModal,
} from '../components/QualiteConfirmationAuditModal';
import FicheDetailModal from '../components/FicheDetailModal';
import { useFicheDetailModal } from '../contexts/FicheDetailModalContext';
import SystemMessageBanner from '../components/SystemMessageBanner';
import ScrollToTopButton from '../components/common/ScrollToTopButton';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { formatRdvDateTime } from '../utils/formatRdvDateTime';
import { generateFicheClientPdf } from '../utils/generateFicheClientPdf';
import { captureFicheTableRowScreenshot } from '../utils/captureFicheTableRowScreenshot';
import { decodeFicheIdFromHash } from '../utils/decodeFicheIdFromHash';
import { getEtatDisplayWithSousEtat, getEtatTableAbbr, isSignerSearchFilter } from '../utils/etatSignerComplet';
import { cleanObservationCQ } from '../utils/cleanObservationCQ';
import { getCentreTableAbbr } from '../utils/tableAbbreviations';
import { getFicheRowByEtatClassName } from '../utils/etatColorContrast';
import { exportToCSV, exportToExcel } from '../utils/exportUtils';
import {
  canExportDashboardResults,
  getDashboardExportColumns,
  buildDashboardExportRows,
} from '../utils/dashboardResultsExport';
import {
  getFicheTableIndicators,
  ficheHasVisibleDecalageRequest,
  getDecalageDisplayInfo,
} from '../utils/ficheTableIndicators';
import {
  usesAdminFicheLayout,
  isHonoreASuivreBadgeSession,
  canToggleFicheBadgeColumns,
  canCustomizeFicheTableColumns,
  getFicheBadgesVisiblePref,
  setFicheBadgesVisiblePref,
  getFicheTableColumnsPref,
} from '../utils/adminMenuUrls';
import { formatFicheCommercialDisplay } from '../utils/ficheCommercialDisplay';
import {
  FicheAdminBadgeHeaders,
  FicheAdminValideCell,
  FicheAdminActionCell,
  FicheAdminIndicatorCells,
} from '../components/fiches/FicheAdminBadgeColumns';
import FicheTableColumnsPicker, { isFicheColumnVisible } from '../components/fiches/FicheTableColumnsPicker';
import { FicheRowSelectHeader, FicheRowSelectCell } from '../components/fiches/FicheRowSelectColumn';
import {
  FicheRowIndexHeader,
  FicheRowIndexCell,
  getFicheRowNumber,
} from '../components/fiches/FicheRowIndexColumn';
import {
  applyForceDesktopViewport,
  applyMobileNativeViewport,
  applyDashboardMobileView,
  applyDashboardTableDesktopView,
  applyDashboardTableDesktopViewForFicheModal,
  isTouchMobileDevice,
} from '../utils/applyForceDesktopViewport';
import {
  stashPendingDashboardFicheModal,
  clearPendingDashboardFicheModal,
  resolvePendingDashboardFicheModal,
} from '../utils/dashboardFicheModalSession';
import {
  useFicheRowContextMenuTouch,
  useFicheContextMenuDismiss,
} from '../utils/ficheRowContextMenuTouch';
import FicheTableMobileDetailButton from '../components/FicheTableMobileDetailButton';
import './Dashboard.css';

const DASHBOARD_MOBILE_NATIVE_CLASS = 'dashboard-page--mobile-native';
const DASHBOARD_EXTRANET_SCROLL_CLASS = 'dashboard-page--extranet-scroll';
const DASHBOARD_OPEN_FICHE_PARAM = 'openFiche';

function getFicheModalStateFromUrl() {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const openFiche = params.get(DASHBOARD_OPEN_FICHE_PARAM);
  if (!openFiche) return null;
  return {
    hash: openFiche,
    focusHistoriqueEtats: params.get('ficheFocusHisto') === '1',
    initialTab: params.get('ficheTab') || undefined,
  };
}

function clearFicheModalUrlParams() {
  const params = new URLSearchParams(window.location.search);
  if (!params.get(DASHBOARD_OPEN_FICHE_PARAM)) return;
  params.delete(DASHBOARD_OPEN_FICHE_PARAM);
  params.delete('tableModal');
  params.delete('ficheFocusHisto');
  params.delete('ficheTab');
  const qs = params.toString();
  window.history.replaceState(null, '', qs ? `${window.location.pathname}?${qs}` : window.location.pathname);
}

function resolveDashboardFicheNumericId(fiche) {
  if (!fiche) return null;
  if (fiche.id != null && Number(fiche.id) > 0) return Number(fiche.id);
  return decodeFicheIdFromHash(fiche.hash);
}

/** Aligné sur le garde-fou backend : fiche_search seul ne doit pas lancer une requête sur toute la table. */
function dashboardUrlHasNarrowingCriteria(params) {
  const q = (k) => {
    const v = params[k];
    return v !== undefined && v !== null && String(v).trim() !== '';
  };
  return (
    q('id_etat_final') ||
    q('id_sous_etat') ||
    q('date_champ') ||
    q('date_debut') ||
    q('date_fin') ||
    q('critere') ||
    q('critere_champ') ||
    q('rdv_valid') ||
    q('rdv_non_valid') ||
    q('rdv_affilie') ||
    q('rdv_non_affilie') ||
    q('sgn_week') ||
    q('sgn_month') ||
    q('sgn_include_pending') ||
    q('prof_ret') ||
    q('prof_celib') ||
    q('include_ko') ||
    q('tel') ||
    q('cp') ||
    q('nom') ||
    q('prenom') ||
    q('produit') ||
    q('id_commercial') ||
    q('id_confirmateur') ||
    q('id_re') ||
    q('id_centre') ||
    q('id_agent') ||
    q('affectation') ||
    q('suivi') ||
    q('day_rdv') ||
    q('ko') ||
    q('hc') ||
    q('annuler_repro_type') ||
    q('qualification_code') ||
    q('stats_drill') ||
    params.include_archive === '1' ||
    params.include_archive === 'true' ||
    q('w') ||
    q('y') ||
    params.yesterday === '1' ||
    params.tomorrow === '1'
  );
}

function getTodayDateRange() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  const dateStr = `${year}-${month}-${day}`;
  return { dateStr, timeStart: '00:00:00', timeEnd: '23:59:59' };
}

const COMMERCIAL_DAY_OFFSET = 5;

function toLocalDateString(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function buildCommercialDayOptions() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const options = [];
  for (let offset = -COMMERCIAL_DAY_OFFSET; offset <= COMMERCIAL_DAY_OFFSET; offset += 1) {
    const d = new Date(today);
    d.setDate(today.getDate() + offset);
    const dateStr = toLocalDateString(d);
    const dayName = d.toLocaleDateString('fr-FR', { weekday: 'long' });
    const dayNum = String(d.getDate()).padStart(2, '0');
    options.push({
      dateStr,
      label: `${dayName} ${dayNum}`,
      isToday: offset === 0,
    });
  }
  return options;
}

const DASHBOARD_MODAL_URL_KEYS = new Set([
  DASHBOARD_OPEN_FICHE_PARAM,
  'tableModal',
  'ficheFocusHisto',
  'ficheTab',
]);

function isDashboardModalBootstrapUrl(params) {
  if (!params.get(DASHBOARD_OPEN_FICHE_PARAM)) return false;
  return [...params.keys()].every((key) => DASHBOARD_MODAL_URL_KEYS.has(key));
}

function filtersToUrlParams(f) {
  const out = {};
  const set = (key, value) => {
    if (value === undefined || value === null) return;
    if (typeof value === 'boolean') {
      if (value) out[key] = '1';
      return;
    }
    const s = String(value).trim();
    if (s === '') return;
    out[key] = s;
  };
  set('fiche_search', true);
  set('page', f.page || 1);
  if (f.limit && f.limit !== 999999) set('limit', f.limit);
  set('id_etat_final', f.id_etat_final);
  set('id_sous_etat', f.id_sous_etat);
  set('annuler_repro_type', f.annuler_repro_type);
  set('date_champ', f.date_champ);
  set('date_debut', f.date_debut);
  set('date_fin', f.date_fin);
  set('time_debut', f.time_debut);
  set('time_fin', f.time_fin);
  set('id_confirmateur', f.id_confirmateur);
  set('id_commercial', f.id_commercial);
  set('id_centre', f.id_centre);
  set('id_agent', f.id_agent);
  set('id_re', f.id_re);
  set('critere', f.critere);
  set('critere_champ', f.critere_champ);
  set('cp', f.cp);
  set('tel', f.tel);
  set('nom', f.nom);
  set('prenom', f.prenom);
  set('ko', f.ko);
  set('include_archive', !!f.include_archive);
  if (f.id_confirmateur) {
    out.include_confirmateur_2 = f.include_confirmateur_2 ? '1' : '0';
  } else {
    set('include_confirmateur_2', !!f.include_confirmateur_2);
  }
  set('stats_drill', f.stats_drill);
  set('stats_drill_source', f.stats_drill_source);
  set('stats_drill_etat', f.stats_drill_etat);
  set('stats_drill_etat_ids', f.stats_drill_etat_ids);
  set('stats_drill_entity', f.stats_drill_entity);
  set('stats_drill_entity_id', f.stats_drill_entity_id);
  set('stats_drill_date_field', f.stats_drill_date_field);
  set('stats_drill_kpi_metric', f.stats_drill_kpi_metric);
  if (f.produit !== undefined && f.produit !== null && f.produit !== '') {
    const p = Array.isArray(f.produit) ? f.produit[0] : f.produit;
    set('produit', p);
  }
  return out;
}

const Dashboard = () => {
  const { user, hasPermission } = useAuth();
  const { setAutoHide, isDesktop, isMobile, closeSidebar } = useSidebar();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = 'confirmed'; // Toujours 'confirmed' puisqu'il n'y a plus d'onglet
  const isPartenaire = Number(user?.fonction) === 9;
  
  useEffect(() => {
    document.body.classList.add('dashboard-page');
    document.documentElement.classList.add('dashboard-page');
    return () => {
      document.body.classList.remove('dashboard-page');
      document.documentElement.classList.remove('dashboard-page');
    };
  }, []);

  /** iOS : rechargement avec ?openFiche=… → tableau zoom out + modal */
  useLayoutEffect(() => {
    if (!isTouchMobileDevice()) return undefined;

    const params = new URLSearchParams(window.location.search);
    const openFiche = params.get(DASHBOARD_OPEN_FICHE_PARAM);
    if (!openFiche) return undefined;

    applyDashboardTableDesktopViewForFicheModal();

    return undefined;
  }, []);

  /** Mobile par défaut (device-width) — bascule vue tableau via le bouton */
  useLayoutEffect(() => {
    if (!isTouchMobileDevice()) return undefined;

    const params = new URLSearchParams(window.location.search);
    if (params.get(DASHBOARD_OPEN_FICHE_PARAM)) return undefined;

    document.documentElement.classList.add(DASHBOARD_MOBILE_NATIVE_CLASS);
    document.body.classList.add(DASHBOARD_MOBILE_NATIVE_CLASS);

    applyMobileNativeViewport();
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(applyMobileNativeViewport);
    });

    return () => {
      cancelAnimationFrame(id);
      document.documentElement.classList.remove(DASHBOARD_MOBILE_NATIVE_CLASS);
      document.body.classList.remove(DASHBOARD_MOBILE_NATIVE_CLASS);
      applyForceDesktopViewport();
    };
  }, []);

  /** Mobile : scroll page unique, tableau desktop (max-content) */
  useLayoutEffect(() => {
    if (!isTouchMobileDevice()) return undefined;

    document.documentElement.classList.add(DASHBOARD_EXTRANET_SCROLL_CLASS);
    document.body.classList.add(DASHBOARD_EXTRANET_SCROLL_CLASS);

    return () => {
      document.documentElement.classList.remove(DASHBOARD_EXTRANET_SCROLL_CLASS);
      document.body.classList.remove(DASHBOARD_EXTRANET_SCROLL_CLASS);
    };
  }, []);
  
  // Pour Confirmateur (fonction 6) et RE Confirmation (fonction 14)
  const isConfirmateur = user?.fonction === 6;
  const isREConfirmation = user?.fonction === 14;
  const isRPConfirmation = user?.fonction === 13;
  const isConfirmateurOrRE = isConfirmateur || isREConfirmation;
  const isQualiteConfirmation = Number(user?.fonction) === 4;
  const isAdminFicheLayout = usesAdminFicheLayout(user);
  const canToggleBadges = canToggleFicheBadgeColumns(user);
  const canCustomizeColumns = canCustomizeFicheTableColumns(user);
  const [showFicheBadges, setShowFicheBadges] = useState(() => getFicheBadgesVisiblePref());
  const [columnVisibility, setColumnVisibility] = useState(() => getFicheTableColumnsPref());
  /** Layout admin complet (ordre colonnes + ACTION au milieu) : admin, backoffice, RP */
  const useBadgeColumnsLayout = isAdminFicheLayout;
  /** Sessions non-admin (ex. confirmateur) : badges optionnels sans changer l’ordre du tableau */
  const showBadgeColumns = !useBadgeColumnsLayout && canToggleBadges && showFicheBadges;
  const isColVisible = useCallback(
    (key) => (canCustomizeColumns ? isFicheColumnVisible(columnVisibility, key) : true),
    [canCustomizeColumns, columnVisibility]
  );
  const showHasBadge = isHonoreASuivreBadgeSession(user);
  /** Menu contextuel (clic droit) sur les lignes du tableau : admin (1), backoffice (11), RP (13), RE (14) */
  const canFicheContextMenu = [1, 11, 13, 14].includes(Number(user?.fonction));
  const [showFilters, setShowFilters] = useState(true);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [exportingResults, setExportingResults] = useState(false);
  const canExportResults = canExportDashboardResults(user);
  const [isTableDesktopView, setIsTableDesktopView] = useState(() => Boolean(getFicheModalStateFromUrl()));
  const isDashboardTouchMobile = isTouchMobileDevice();

  const switchToMobileView = useCallback(() => {
    document.documentElement.classList.add(DASHBOARD_MOBILE_NATIVE_CLASS);
    document.body.classList.add(DASHBOARD_MOBILE_NATIVE_CLASS);
    applyDashboardMobileView();
    window.dispatchEvent(new Event('viewport-layout-change'));
    closeSidebar();
    setIsTableDesktopView(false);
  }, [closeSidebar]);

  // Téléphone : sidebar masquée par défaut à l'arrivée sur le dashboard
  useEffect(() => {
    if (isDashboardTouchMobile || isMobile) {
      closeSidebar();
    }
    // Uniquement au montage : ne pas re-fermer si l'utilisateur rouvre le menu
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const switchToTableDesktopView = useCallback(() => {
    document.documentElement.classList.remove(DASHBOARD_MOBILE_NATIVE_CLASS);
    document.body.classList.remove(DASHBOARD_MOBILE_NATIVE_CLASS);
    applyDashboardTableDesktopView();
    setIsTableDesktopView(true);
  }, []);

  const toggleDashboardViewport = useCallback(() => {
    if (isTableDesktopView) switchToMobileView();
    else switchToTableDesktopView();
  }, [isTableDesktopView, switchToMobileView, switchToTableDesktopView]);

  // Par défaut : filtre vide
  const getInitialFilters = () => ({
    page: 1,
    limit: 999999,
    fiche_search: false,
    id_etat_final: '',
    date_champ: '',
    date_debut: '',
    date_fin: '',
    time_debut: '',
    time_fin: '',
    include_archive: false,
    ko: '', // '' = tous, '0' = fiches OK, '1' = fiches KO
    id_centre: '',
    id_sous_etat: '',
    annuler_repro_type: '', // '' = tous, 'compte_rendu' ou 'repro_confirmateurs' (visible si état = Annuler à reprogrammer ou Client honoré à suivre)
    include_confirmateur_2: false,
  });

  /** Filtres initiaux : confirmateur (6) — case « inclure 2e confirmateur » décochée (réservée à une recherche explicite). */
  const getFiltersForUser = (u) => {
    const base = getInitialFilters();
    if (u?.fonction === 6) return { ...base, include_confirmateur_2: false };
    return base;
  };
  const [filters, setFilters] = useState(getInitialFilters);
  // Filtres appliqués à la requête (mis à jour uniquement au clic sur Recherche, pagination ou reset)
  const [appliedFilters, setAppliedFilters] = useState(getInitialFilters);
  const appliedFiltersRef = useRef(appliedFilters);
  appliedFiltersRef.current = appliedFilters;
  /** Vue liste depuis la carte « RDV à venir » sans modifier l’URL ni les champs du formulaire */
  const [statsListOverride, setStatsListOverride] = useState(null);

  const applyDashboardDefaultsFromEmptyUrl = useCallback(() => {
    if (!user) return;
    setStatsListOverride(null);
    const { dateStr, timeStart, timeEnd } = getTodayDateRange();
    const emptyUiFilters = getFiltersForUser(user);
    // Confirmateur : liste du jour sans toucher au filtre (backend = dernière ligne fiches_histo).
    // Autres profils : fiches confirmées (état 7) du jour via fiches_histo_confirmation (aligné stats « journée »).
    if (user.fonction === 6) {
      const defaultApplied = {
        ...getInitialFilters(),
        page: 1,
        limit: 999999,
        fiche_search: true,
        id_etat_final: '',
        date_champ: 'fiches_histo',
        date_debut: dateStr,
        date_fin: dateStr,
        time_debut: timeStart,
        time_fin: timeEnd,
        include_confirmateur_2: false,
      };
      setFilters(emptyUiFilters);
      setAppliedFilters(defaultApplied);
    } else {
      const defaultApplied = {
        ...getInitialFilters(),
        page: 1,
        limit: 999999,
        fiche_search: true,
        id_etat_final: 7,
        date_champ: 'fiches_histo_confirmation',
        date_debut: dateStr,
        date_fin: dateStr,
        time_debut: timeStart,
        time_fin: timeEnd,
      };
      setFilters(emptyUiFilters);
      setAppliedFilters(defaultApplied);
    }
    setShowFilters(true);
  }, [user]);

  // Lire l’URL ; sans query : défaut confirmateur = actions du jour (fiches_histo), autres = confirmées du jour
  useEffect(() => {
    const urlParams = Object.fromEntries(searchParams.entries());
    const hasUrl = Object.keys(urlParams).length > 0;

    if (hasUrl && isDashboardModalBootstrapUrl(searchParams) && user) {
      applyDashboardDefaultsFromEmptyUrl();
      return;
    }

    if (
      hasUrl &&
      urlParams.fiche_search === '1' &&
      !dashboardUrlHasNarrowingCriteria(urlParams) &&
      !searchParams.get(DASHBOARD_OPEN_FICHE_PARAM)
    ) {
      setSearchParams({}, { replace: true });
      return;
    }
    if (hasUrl && urlParams.fiche_search === '1') {
      setStatsListOverride(null);
      const cleanedUrlParams = { ...urlParams };
      DASHBOARD_MODAL_URL_KEYS.forEach((key) => {
        delete cleanedUrlParams[key];
      });
      const newFilters = {
        page: parseInt(urlParams.page) || 1,
        limit: parseInt(urlParams.limit) || 999999,
        fiche_search: true,
        ...cleanedUrlParams
      };
      if (newFilters.id_etat_final != null && newFilters.id_etat_final !== '') {
        const etatRaw = String(newFilters.id_etat_final);
        if (etatRaw === 't_s') {
          newFilters.id_etat_final = 't_s';
        } else {
          const etatNum = parseInt(etatRaw, 10);
          if (!Number.isNaN(etatNum)) {
            newFilters.id_etat_final = etatNum;
          }
        }
      }
      if (newFilters.include_confirmateur_2 !== undefined && newFilters.include_confirmateur_2 !== null) {
        const v = newFilters.include_confirmateur_2;
        newFilters.include_confirmateur_2 =
          v === true || v === 1 || v === '1' || v === 'true';
      }
      // Ne pas pré-remplir dates / état depuis l’URL : uniquement ce qui est dans les paramètres
      setFilters(newFilters);
      setAppliedFilters(newFilters);
      setShowFilters(true);
    } else if (Object.keys(urlParams).length === 0 && user) {
      applyDashboardDefaultsFromEmptyUrl();
    }
  }, [searchParams, user, applyDashboardDefaultsFromEmptyUrl]);

  useEffect(() => {
    const onLogoDashboardHome = () => {
      if (user) applyDashboardDefaultsFromEmptyUrl();
    };
    window.addEventListener('dashboard-reset-default', onLogoDashboardHome);
    return () => window.removeEventListener('dashboard-reset-default', onLogoDashboardHome);
  }, [user, applyDashboardDefaultsFromEmptyUrl]);

  // Ref pour le champ critère dans le modal (focus à l'ouverture)
  const searchModalCritereRef = useRef(null);
  useEffect(() => {
    if (showSearchModal && searchModalCritereRef.current && user?.fonction !== 5) {
      const t = setTimeout(() => {
        searchModalCritereRef.current?.focus();
      }, 100);
      return () => clearTimeout(t);
    }
  }, [showSearchModal, user?.fonction]);

  /** null | { hash: string, focusHistoriqueEtats?: boolean } */
  const [ficheDetailModal, setFicheDetailModal] = useState(() =>
    resolvePendingDashboardFicheModal(getFicheModalStateFromUrl())
  );
  const [ficheContextMenu, setFicheContextMenu] = useState(null);
  const openFicheContextMenu = useCallback((e, fiche) => {
    if (!canFicheContextMenu) return;
    e.preventDefault();
    e.stopPropagation();
    setFicheContextMenu({ x: e.clientX, y: e.clientY, fiche, openedAt: Date.now() });
  }, [canFicheContextMenu]);
  const { bindFicheRowContextMenu } = useFicheRowContextMenuTouch(openFicheContextMenu);
  useFicheContextMenuDismiss(ficheContextMenu, setFicheContextMenu);
  const [auditFiche, setAuditFiche] = useState(null);
  const [etatModalFiche, setEtatModalFiche] = useState(null);
  const [etatModalNewId, setEtatModalNewId] = useState('');
  const [etatModalMotif, setEtatModalMotif] = useState('');
  const [etatModalSousEtat, setEtatModalSousEtat] = useState('');
  const [affectModalFiche, setAffectModalFiche] = useState(null);
  const [affectModalCommercialId, setAffectModalCommercialId] = useState('');
  const [validationModalFiche, setValidationModalFiche] = useState(null);
  const [validationConfRdvAvec, setValidationConfRdvAvec] = useState('');
  const [validationConfPresenceCouple, setValidationConfPresenceCouple] = useState('');
  const { lastViewedFicheHash, setLastViewedFicheHash } = useFicheDetailModal();

  /** iOS / mobile : rechargement page → tableau zoom out + modal */
  const openDashboardFicheDetail = useCallback(
    (modalState) => {
      if (!modalState?.hash) return;

      if (isDashboardTouchMobile) {
        stashPendingDashboardFicheModal(modalState);
        const params = new URLSearchParams(window.location.search);
        if (!params.get('fiche_search') && appliedFiltersRef.current?.fiche_search) {
          Object.entries(filtersToUrlParams(appliedFiltersRef.current)).forEach(([key, value]) => {
            params.set(key, String(value));
          });
        }
        params.set(DASHBOARD_OPEN_FICHE_PARAM, modalState.hash);
        if (modalState.focusHistoriqueEtats) params.set('ficheFocusHisto', '1');
        if (modalState.initialTab) params.set('ficheTab', modalState.initialTab);
        params.set('tableModal', '1');
        const qs = params.toString();
        window.location.assign(qs ? `${window.location.pathname}?${qs}` : window.location.pathname);
        return;
      }

      setFicheDetailModal(modalState);
      if (modalState.hash) {
        setLastViewedFicheHash(modalState.hash);
      }
    },
    [isDashboardTouchMobile, setLastViewedFicheHash]
  );

  useEffect(() => {
    if (!isDashboardTouchMobile) return undefined;

    const fromUrl = getFicheModalStateFromUrl();
    const pending = resolvePendingDashboardFicheModal(fromUrl);
    if (!pending?.hash) return undefined;

    setIsTableDesktopView(true);
    setFicheDetailModal(pending);
    setLastViewedFicheHash(pending.hash);
    if (fromUrl) clearFicheModalUrlParams();

    return undefined;
  }, [isDashboardTouchMobile, setLastViewedFicheHash]);

  const closeDashboardFicheDetail = useCallback(() => {
    clearPendingDashboardFicheModal();
    setFicheDetailModal(null);
    if (isDashboardTouchMobile) {
      switchToMobileView();
    } else {
      closeSidebar();
    }
  }, [isDashboardTouchMobile, switchToMobileView, closeSidebar]);

  const [sortConfig, setSortConfig] = useState({
    key: 'date_rdv_time', // Tri par défaut sur la date de RDV
    direction: 'asc', // 'asc' or 'desc'
  });
  const [showConfirmateursTable, setShowConfirmateursTable] = useState(false); // Fermé par défaut
  const [quickSearch, setQuickSearch] = useState(''); // Recherche rapide
  const debouncedQuickSearch = useDebouncedValue(quickSearch, 250);

  const normalizeText = (v) => (typeof v === 'string' ? v.trim() : v);

  // Récupérer les données de référence
  const { data: centresData } = useQuery('centres', async () => {
    const res = await api.get('/management/centres');
    return res.data.data;
  });

  const { data: usersData } = useQuery('users', async () => {
    const res = await api.get('/management/utilisateurs');
    return res.data.data;
  });

  const { data: sousEtatsData } = useQuery('sous-etat', async () => {
    const res = await api.get('/management/sous-etat');
    return res.data.data || [];
  }, { staleTime: 5 * 60 * 1000 });

  const { data: etatsData, isLoading: isLoadingEtats, error: etatsError } = useQuery('etats', async () => {
    const res = await api.get('/management/etats');
    return res.data.data;
  });

  // Récupérer les produits
  const { data: produitsData } = useQuery('produits', async () => {
    try {
      const res = await api.get('/management/produits');
      return res.data.data || [];
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
      return [];
    }
  });

  // Construire les paramètres selon l'onglet actif (utilise appliedFilters = filtres envoyés à l'API)
  const getQueryParams = (sourceFilters) => {
    const src = sourceFilters || appliedFilters;
    const { dateStr, timeStart, timeEnd } = getTodayDateRange();
    const isQuickSearchActive = debouncedQuickSearch.trim() !== '';
    const limitParam = isQuickSearchActive ? 999999 : (src.limit === 999999 ? 999999 : src.limit);
    const pageParam = isQuickSearchActive ? 1 : (src.page || 1);

    /* Carte « RDV à venir » : état 7, date_rdv >= aujourd’hui ; confirmateur = id_confirmateur uniquement (pas 2/3) */
    if (statsListOverride === 'upcoming') {
      const upcomingParams = {
        fiche_search: 1,
        page: pageParam,
        limit: limitParam,
        id_etat_final: 7,
        date_champ: 'date_rdv_time',
        date_debut: dateStr,
        time_debut: '00:00:00',
        include_confirmateur_2: 0,
      };
      if (src.id_confirmateur) {
        upcomingParams.id_confirmateur = src.id_confirmateur;
      }
      upcomingParams.sort_by = sortConfig.key || 'date_rdv_time';
      upcomingParams.sort_dir = sortConfig.direction || 'asc';
      // RE Confirmation : toute la liste des RDV à venir (pas de filtre équipe)
      return upcomingParams;
    }
    
    if (src.fiche_search) {
      const searchParams = { 
        ...src, 
        limit: limitParam,
        page: pageParam,
        fiche_search: 1,
        sort_by: sortConfig.key || 'date_rdv_time',
        sort_dir: sortConfig.direction || 'asc',
      };

      // Normaliser le critère (enlever espaces avant/après)
      if (typeof searchParams.critere === 'string') {
        searchParams.critere = searchParams.critere.trim();
      }
      // Nom et Prénom sont dans le type de critère : ne pas envoyer nom/prenom en double
      const champ = searchParams.critere_champ || 'tel';
      if (champ === 'nom' || champ === 'prenom') {
        delete searchParams.nom;
        delete searchParams.prenom;
      }

      // Nettoyer les paramètres vides (mais garder page, limit, fiche_search, critere, critere_champ)
      Object.keys(searchParams).forEach(key => {
        if (key === 'page' || key === 'limit' || key === 'fiche_search' || key === 'sort_by' || key === 'sort_dir') {
          return; // Ne pas supprimer ces paramètres
        }
        // include_archive: n'envoyer au backend que si activé
        if (key === 'include_archive') {
          if (searchParams.include_archive) {
            searchParams.include_archive = 1;
          } else {
            delete searchParams.include_archive;
          }
          return;
        }
        // include_confirmateur_2 : avec id_confirmateur → toujours 0 ou 1 ; confirmateur (6) sans filtre → uniquement si coché
        if (key === 'include_confirmateur_2') {
          if (searchParams.id_confirmateur) {
            searchParams.include_confirmateur_2 = searchParams.include_confirmateur_2 ? 1 : 0;
          } else if (user?.fonction === 6) {
            if (searchParams.include_confirmateur_2) {
              searchParams.include_confirmateur_2 = 1;
            } else {
              searchParams.include_confirmateur_2 = 0;
            }
          } else {
            delete searchParams.include_confirmateur_2;
          }
          return;
        }
        // Si critere est rempli, garder critere_champ même s'il est vide (utiliser la valeur par défaut)
        if (key === 'critere_champ' && searchParams.critere) {
          // Garder critere_champ avec la valeur par défaut 'tel' si vide
          if (!searchParams.critere_champ) {
            searchParams.critere_champ = 'tel';
          }
          return;
        }
        // IMPORTANT: le critère doit se combiner avec les autres filtres (date, état, centre, etc.).
        // On ne supprime plus les paramètres de date/time quand "critere" est renseigné.
        if (searchParams[key] === '' || searchParams[key] === null || searchParams[key] === undefined) {
          delete searchParams[key];
        }
      });

      return searchParams;
    }
    
    // Sinon, appliquer les filtres par défaut selon l'onglet actif
    const baseParams = { 
      page: pageParam,
      limit: limitParam
    };

    if (src.include_archive) {
      baseParams.include_archive = 1;
    }
    
    const defaultParams = {
      ...baseParams,
      fiche_search: 1,
      date_champ: '',
      date_debut: '',
      date_fin: '',
      time_debut: '',
      time_fin: '',
      sort_by: sortConfig.key || 'date_rdv_time',
      sort_dir: sortConfig.direction || 'asc',
    };
    if (src.id_centre) {
      defaultParams.id_centre = src.id_centre;
    }
    return defaultParams;
  };

  // Récupérer les statistiques des RDV
  const { data: dashboardStats, isLoading: isLoadingStats, error: statsError } = useQuery(
    'dashboard-stats',
    async () => {
      const res = await api.get('/statistiques/dashboard');
      return res.data.data;
    },
    {
      refetchInterval: 60000, // Rafraîchir toutes les minutes
    }
  );

  // Récupérer les fiches (requête lancée uniquement au clic Recherche, pagination ou reset)
  const { data, isLoading, isFetching, error, refetch } = useQuery(
    ['fiches', appliedFilters, activeTab, debouncedQuickSearch, statsListOverride, sortConfig],
    async () => {
      const params = getQueryParams();
      const response = await api.get('/fiches', { params });
      return response.data;
    },
    { keepPreviousData: true, enabled: !!appliedFilters.fiche_search || statsListOverride === 'upcoming' }
  );

  const updateEtatFromMenuMutation = useMutation(
    async ({ hash, body }) => {
      const res = await api.put(`/fiches/${hash}`, body);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['fiches']);
        setEtatModalFiche(null);
        setEtatModalMotif('');
        setEtatModalSousEtat('');
      },
      onError: (err) => {
        alert(err.response?.data?.message || err.message || 'Erreur lors du changement d\'état');
      },
    }
  );

  const affectFromMenuMutation = useMutation(
    async ({ fiches_ids, id_commercial }) => {
      const res = await api.post('/affectations/affecter', {
        fiches_ids,
        id_commercial: parseInt(id_commercial, 10),
      });
      return res.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['fiches']);
        setAffectModalFiche(null);
        setAffectModalCommercialId('');
        const errs = data?.data?.erreurs;
        if (errs?.length) {
          alert(errs.map((e) => `${e.fiche_id}: ${e.error}`).join('\n'));
        }
      },
      onError: (err) => {
        alert(err.response?.data?.message || err.message || 'Erreur lors de l\'affectation');
      },
    }
  );

  const desaffectFromMenuMutation = useMutation(
    async ({ fiches_ids }) => {
      const res = await api.post('/affectations/desaffecter', { fiches_ids });
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['fiches']);
        setAffectModalFiche(null);
        setAffectModalCommercialId('');
      },
      onError: (err) => {
        alert(err.response?.data?.message || err.message || 'Erreur lors de la désaffectation');
      },
    }
  );

  const validateFromMenuMutation = useMutation(
    async ({ hash, type_valid, conf_rdv_avec, conf_presence_couple }) => {
      const payload = { type_valid };
      if (String(type_valid) !== '0') {
        payload.conf_rdv_avec = conf_rdv_avec ?? null;
        payload.conf_presence_couple = conf_presence_couple ?? null;
      }
      const res = await api.post(`/fiches/${hash}/valider`, payload);
      return res.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['fiches']);
        setValidationModalFiche(null);
        setValidationConfRdvAvec('');
        setValidationConfPresenceCouple('');
        alert(data?.message || 'Enregistré.');
      },
      onError: (err) => {
        alert(err.response?.data?.message || err.message || 'Erreur lors de la validation');
      },
    }
  );

  const generatePdfFromMenuMutation = useMutation(
    async (hash) => {
      const [ficheRes, profRes, tcRes] = await Promise.all([
        api.get(`/fiches/${encodeURIComponent(hash)}`),
        api.get('/management/professions'),
        api.get('/management/type-contrat'),
      ]);
      const fiche = ficheRes.data?.data;
      if (!fiche) throw new Error('Fiche introuvable');
      const users = usersData || [];
      const agents = users.filter((u) => Number(u.fonction) === 3);
      const commerciauxList = users.filter((u) => Number(u.fonction) === 5 && u.etat > 0);
      const confirmateursList = users.filter(
        (u) => Number(u.fonction) === 6 && (u.etat > 0 || u.etat == null)
      );
      const centresList = centresData ? centresData.filter((c) => c.etat > 0) : [];
      generateFicheClientPdf(fiche, {
        professions: profRes.data?.data || [],
        typeContrat: tcRes.data?.data || [],
        centres: centresList,
        agents,
        commerciaux: commerciauxList,
        confirmateurs: confirmateursList,
      });
    },
    {
      onError: (err) => {
        alert(err.response?.data?.message || err.message || 'Impossible de générer le PDF');
      },
    }
  );

  // Filtrer les utilisateurs par fonction
  const confirmateurs = usersData ? usersData.filter(u => u.fonction === 6 && u.etat > 0) : [];
  const commerciaux = usersData ? usersData.filter(u => u.fonction === 5 && u.etat > 0) : [];
  const centres = centresData ? centresData.filter(c => c.etat > 0) : [];
  /** Profils multi-centres / gestion : même liste qu’en recherche avancée (excl. commercial seul). */
  const showCentreDashboardFilter =
    [1, 2, 3, 6, 7, 8, 9, 11, 12, 13, 14].includes(Number(user?.fonction)) && centres.length > 0;
  // Groupe 0 visible uniquement par RE qualification (2), Agent qualification (3), Qualité qualification (8), RP qualification (12)
  const canSeeGroupe0 = [2, 3, 8, 12].includes(Number(user?.fonction));
  const etats = (etatsData || []).filter(e => {
    if (String(e.groupe) === '0' || e.groupe === 0) return canSeeGroupe0;
    return true;
  });

  // Grouper les états par phase
  const normalizeTitre = (t) => (!t ? '' : String(t).toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '').trim());
  const CONFIRMATEUR_ETATS_PHASE2 = [
    'confirmer',
    'annuler a reprogrammer',
    'annuler et a reprogrammer', // libellé base « ANNULER ET A REPROGRAMMER »
    'client honore a suivre',
    'honore hors cible confirmateurs',
    'rdv annuler',
    'refuser'
  ];
  const isEtatAllowedForConfirmateur = (e, allowedList) => {
    const id = Number(e.id);
    if (id === 8) return true; // Annuler à reprogrammer — toujours proposé en session confirmateur
    const n = normalizeTitre(e.titre);
    return allowedList.some(a => n === a || n.includes(a) || a.includes(n));
  };
  /** Phase 3 confirmateur : uniquement l'option "SIGNER" dans le filtre (pas Signer retracter, Signer PM, Signer complet) */
  const isEtatAllowedForConfirmateurPhase3 = (e) => {
    const n = normalizeTitre(e.titre);
    if (n.includes('retracter') || n.includes('pm') || n.includes('2 fois') || n.includes('complet')) return false;
    return n === 'signer';
  };

  let etatsPhase0 = etats.filter(e => String(e.groupe) === '0' || e.groupe === 0);
  let etatsPhase1 = etats.filter(e => String(e.groupe) === '1' || e.groupe === 1);
  let etatsPhase2 = etats.filter(e => String(e.groupe) === '2' || e.groupe === 2);
  let etatsPhase3 = etats.filter(e => String(e.groupe) === '3' || e.groupe === 3);

  // Session confirmateur (fonction 6) : uniquement certains états en phase 2 et phase 3 (phase 3 = uniquement "Signer")
  if (Number(user?.fonction) === 6) {
    etatsPhase0 = [];
    etatsPhase1 = [];
    etatsPhase2 = etatsPhase2.filter(e => isEtatAllowedForConfirmateur(e, CONFIRMATEUR_ETATS_PHASE2));
    etatsPhase3 = etatsPhase3.filter(e => isEtatAllowedForConfirmateurPhase3(e));
    // « Annuler à reprogrammer » (id 8) est souvent en phase 0/1 en base : il était exclu du filtre. On l’ajoute pour la recherche.
    const etatAnnulerRepro = ((etatsData || []).find((e) => Number(e.id) === 8))
      || (etats || []).find((e) => Number(e.id) === 8);
    if (etatAnnulerRepro && !etatsPhase2.some((e) => Number(e.id) === 8)) {
      etatsPhase2 = [etatAnnulerRepro, ...etatsPhase2];
    }
    const etatHonoreASuivre = ((etatsData || []).find((e) => Number(e.id) === 9))
      || (etats || []).find((e) => Number(e.id) === 9);
    if (etatHonoreASuivre && !etatsPhase2.some((e) => Number(e.id) === 9)) {
      etatsPhase2 = [etatHonoreASuivre, ...etatsPhase2];
    }
  }


  const sousEtatsForSelectedEtat = (sousEtatsData || []).filter(
    s => Number(s.id_etat) === Number(filters.id_etat_final)
  );
  const showSousEtatFilter =
    filters.id_etat_final &&
    String(filters.id_etat_final) !== 't_s' &&
    sousEtatsForSelectedEtat.length > 0;
  const showSourceFilter =
    Number(filters.id_etat_final) === 8 || Number(filters.id_etat_final) === 9;
  const showCqColumns =
    !!appliedFilters.fiche_search &&
    statsListOverride !== 'upcoming' &&
    isSignerSearchFilter(appliedFilters.id_etat_final, etatsData || []);

  if (etatsError) {
    console.error('Erreur lors du chargement des états:', etatsError);
  }

  const sousEtatsForEtatModal = (sousEtatsData || []).filter(
    (s) => etatModalNewId && Number(s.id_etat) === Number(etatModalNewId)
  );

  const handleFilterChange = (key, value) => {
    // Ne pas trim à la frappe: sinon l'espace de séparation des mots est supprimé
    // (ex: "BEN AHMED" devient difficile à saisir). Le trim est fait avant envoi API.
    const nextValue = value;
    setFilters(prev => ({
      ...prev,
      [key]: nextValue,
      ...(key === 'id_etat_final' ? { id_sous_etat: '', annuler_repro_type: '' } : {}),
      ...(key === 'id_confirmateur'
        ? { include_confirmateur_2: false }
        : {}),
      page: key === 'page' ? value : 1
    }));
    // Pagination et limite : mettre à jour appliedFilters pour lancer la requête
    if (key === 'page' || key === 'limit') {
      setAppliedFilters(prev => ({
        ...prev,
        [key]: key === 'page' ? value : nextValue,
        ...(key === 'limit' ? { page: 1 } : {})
      }));
    }
  };

  /** Valeur contrôlée pour input[type="datetime-local"] : YYYY-MM-DDTHH:mm */
  const filterToDatetimeLocalValue = (dateValue, timeValue, defaultTime = '00:00:00') => {
    const d = String(dateValue || '').trim();
    if (!d) return '';
    const tRaw = String(timeValue || defaultTime || '').trim();
    const hhmm = tRaw.length >= 5 ? tRaw.slice(0, 5) : String(defaultTime || '00:00:00').slice(0, 5);
    return `${d}T${hhmm}`;
  };

  const handleDatetimeLocalChange = (bound, e) => {
    const v = e.target.value;
    if (!v) {
      handleFilterChange(bound === 'debut' ? 'date_debut' : 'date_fin', '');
      handleFilterChange(bound === 'debut' ? 'time_debut' : 'time_fin', '');
      return;
    }
    const [datePart, timePart] = v.split('T');
    const hhmm =
      timePart && timePart.length >= 5
        ? timePart.slice(0, 5)
        : bound === 'debut'
          ? '00:00'
          : '23:59';
    const timeWithSeconds = `${hhmm}:00`;
    handleFilterChange(bound === 'debut' ? 'date_debut' : 'date_fin', datePart || '');
    handleFilterChange(bound === 'debut' ? 'time_debut' : 'time_fin', timeWithSeconds);
  };

  /** Remplit date début ou fin avec la date/heure actuelle */
  const applyNowToDatetimeBound = (bound) => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const dateStr = `${yyyy}-${mm}-${dd}`;
    const timeStr = `${hh}:${min}:00`;
    setFilters((prev) => ({
      ...prev,
      ...(bound === 'debut'
        ? { date_debut: dateStr, time_debut: timeStr }
        : { date_fin: dateStr, time_fin: timeStr }),
      page: 1,
    }));
  };

  /** À la sélection d’un champ de date : plage par défaut = jour courant, 00:00 → 23:59 */
  const handleDateChampChange = (value) => {
    setFilters((prev) => {
      const next = { ...prev, date_champ: value, page: 1 };
      if (value) {
        const { dateStr, timeStart, timeEnd } = getTodayDateRange();
        next.date_debut = dateStr;
        next.date_fin = dateStr;
        next.time_debut = timeStart;
        next.time_fin = timeEnd;
      } else {
        next.date_debut = '';
        next.date_fin = '';
        next.time_debut = '';
        next.time_fin = '';
      }
      return next;
    });
  };

  const handlePageChange = (newPage) => {
    handleFilterChange('page', newPage);
  };

  /**
   * Convertit l'objet de filtres en paramètres d'URL lisibles, en filtrant les valeurs vides.
   * Sert à conserver les critères de recherche dans l'URL : ainsi un rafraîchissement de la page
   * relance la même recherche (cf. useEffect qui lit `searchParams` au montage).
   */
  const handleSearch = async (e) => {
    e.preventDefault();
    // Afficher la confirmation uniquement si la recherche n'est pas affinée (risque de recherche lourde)
    const hasRefinedSearch = !!(
      (filters.critere || '').trim() ||
      filters.id_etat_final ||
      filters.date_champ ||
      filters.date_debut ||
      filters.date_fin ||
      filters.id_confirmateur ||
      filters.id_commercial ||
      filters.id_centre ||
      (filters.cp || '').trim() ||
      filters.produit
    );
    if (!hasRefinedSearch && !window.confirm('Cette recherche peut prendre plusieurs secondes. Confirmer la recherche ?')) {
      return;
    }
    setIsSearching(true);
    const newFilters = { ...filters, fiche_search: true, page: 1 };

    // Recherche par critère (nom/tél) sans champ de date choisi : retirer la plage
    // « aujourd'hui » par défaut pour ne pas restreindre à tort.
    // Ne PAS appliquer si date_champ est renseigné (ex. Date Planning + département) :
    // sinon le périmètre de dates disparaît et le backend ignore la période.
    const hasExplicitDateChamp = !!(newFilters.date_champ || '').trim();
    if ((newFilters.critere || '').trim() && !hasExplicitDateChamp) {
      const today = getTodayDateRange().dateStr;
      if (newFilters.date_debut === today && newFilters.date_fin === today) {
        delete newFilters.date_debut;
        delete newFilters.date_fin;
        delete newFilters.date_champ;
        delete newFilters.time_debut;
        delete newFilters.time_fin;
      }
    }

    setFilters(newFilters);
    setAppliedFilters(newFilters);
    setStatsListOverride(null);
    // Persister la recherche dans l'URL : le rafraîchissement de la page rejoue la même requête.
    setSearchParams(filtersToUrlParams(newFilters), { replace: true });
  };

  const handleReset = () => {
    const initial = getFiltersForUser(user);
    setFilters(initial);
    setAppliedFilters(initial);
    setStatsListOverride(null);
    // Nettoyer l'URL pour revenir au comportement par défaut au prochain rafraîchissement.
    setSearchParams({}, { replace: true });
  };

  const commercialDayOptions = buildCommercialDayOptions();
  const isCommercialRdvView = Number(appliedFilters.id_commercial) > 0 && appliedFilters.fiche_search;
  const commercialDayValue = commercialDayOptions.some((d) => d.dateStr === String(appliedFilters.date_debut || ''))
    ? String(appliedFilters.date_debut)
    : getTodayDateRange().dateStr;

  const handleCommercialDayChange = (dateStr) => {
    const newFilters = {
      ...appliedFilters,
      fiche_search: true,
      page: 1,
      date_champ: 'date_rdv_time',
      date_debut: dateStr,
      date_fin: dateStr,
      time_debut: '00:00:00',
      time_fin: '23:59:59',
    };
    setFilters(newFilters);
    setAppliedFilters(newFilters);
    setStatsListOverride(null);
    setSearchParams(filtersToUrlParams(newFilters), { replace: true });
  };

  // Réinitialiser isSearching quand la requête est terminée
  useEffect(() => {
    if (!isFetching && isSearching) {
      setIsSearching(false);
    }
  }, [isFetching, isSearching]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Obtenir la couleur de l'état : priorité à etat_color renvoyé par l'API (couvre tous les états dont ceux hors filtre confirmateur), sinon etatsData
  const getEtatColor = (etatId, fiche) => {
    if (fiche?.etat_color) return fiche.etat_color;
    const etat = (etatsData || []).find(e => e.id === etatId || e.id === Number(etatId));
    return etat?.color || '#cccccc';
  };

  // Obtenir le nom du produit
  const getProduitName = (produitId) => {
    return produitId === 1 ? 'PAC' : produitId === 2 ? 'PV' : '';
  };

  // Obtenir la couleur du produit
  const getProduitColor = (produitId) => {
    return produitId === 1 ? '#66D5D4' : produitId === 2 ? '#818cf8' : '#cccccc';
  };

  const getDecalageDashboardInfo = (fiche) => {
    const info = getDecalageDisplayInfo(fiche);
    if (!info || info.status === 'cancelled') return null;
    return info;
  };

  // Vérifier les indicateurs badges (SIG, CS, R2, HAS, RF, ANN)
  const checkIndicators = (histoString, fiche = {}) =>
    getFicheTableIndicators(histoString, fiche, etatsData || []);

  // Obtenir le nom de l'utilisateur
  const getUserName = (userId) => {
    if (!userId || !usersData) return '';
    const user = usersData.find(u => u.id === userId);
    return user?.pseudo || '';
  };

  // Affichage commercial : en recherche signatures → commercial ayant signé (histo)
  const getCommercialsFormatted = (fiche) =>
    formatFicheCommercialDisplay(fiche, getUserName, appliedFilters);

  // Obtenir le nom du centre (complet — exports / tooltip)
  const getCentreName = (centreId) => {
    if (!centreId || !centresData) return '';
    const centre = centresData.find(c => c.id === centreId);
    return centre?.titre || '';
  };

  const getCentreDisplay = (centreId) => getCentreTableAbbr(centreId, centresData);

  // Obtenir le nom de l'état
  const getEtatName = (etatId) => {
    if (!etatId) return '';
    const etat = etats.find(e => e.id === etatId);
    return etat?.titre || '';
  };

  const sousEtats = sousEtatsData || [];

  // Libellé d'état tableau : abbreviation BDD (+ sous-état abrégé si Signer)
  const getEtatDisplayName = (fiche) =>
    getEtatTableAbbr(fiche, etats, sousEtats).trim() ||
    getEtatName(fiche?.id_etat_final) ||
    '';

  const getEtatFullLabel = (fiche) =>
    getEtatDisplayWithSousEtat(fiche, etats, sousEtats).trim() ||
    getEtatName(fiche?.id_etat_final) ||
    '';

  // Bulle au survol : nom, téléphone, puis commentaire.
  // — État actuel issu d’un compte rendu (dernière ligne fiches_histo.from_compte_rendu) → commentaire commercial (fiches).
  // — Sinon, si état ≠ Confirmer (7) → commentaire saisi au changement d’état (conf_commentaire_produit de la dernière ligne fiches_histo).
  // — Sinon (Confirmer) → conf_commentaire_produit sur la fiche.
  const getTooltipComment = (fiche) => {
    const nom = (fiche?.nom ?? '').trim();
    const prenom = (fiche?.prenom ?? '').trim();
    const tel = (fiche?.tel ?? '').trim();
    const etatCr = fiche?.current_state_from_compte_rendu === true;
    const idEtat = Number(fiche?.id_etat_final);
    const isConfirmer = idEtat === 7;
    let commentaire = '';
    if (etatCr) {
      commentaire = (fiche?.commentaire_commercial ?? '').trim();
    } else if (!isConfirmer) {
      commentaire = (fiche?.histo_last_conf_commentaire ?? '').trim();
    } else {
      commentaire = (fiche?.conf_commentaire_produit ?? '').trim();
    }
    const commentaireStr = commentaire.length > 500 ? commentaire.slice(0, 497) + '...' : commentaire;
    const lignes = [
      [nom, prenom].filter(Boolean).join(' '),
      tel,
      commentaireStr
    ].filter(Boolean);
    return lignes.join('\n');
  };

  // id_etat 8 = Annuler à reprogrammer, 9 = Client honoré à suivre : <CR> si l'état ACTUEL vient d'un compte rendu
  const ID_ETAT_ANNULER_A_REPROGRAMMER = 8;
  const ID_ETAT_HONORE_A_SUIVRE = 9;
  const showCRPrefix = (fiche) =>
    (Number(fiche?.id_etat_final) === ID_ETAT_ANNULER_A_REPROGRAMMER ||
      Number(fiche?.id_etat_final) === ID_ETAT_HONORE_A_SUIVRE) &&
    fiche?.current_state_from_compte_rendu === true;

  /** Aligné détails fiche : Confirmer, Honoré à suivre, Signer → confirmateurs 1, 2 et 3 sur la fiche ; sinon dernière ligne histo puis fiche. */
  const ETATS_DASHBOARD_CONF123 = [7, 9, 13, 16, 44, 45];
  const getConfirmateursFormatted = (fiche) => {
    const etatId = Number(fiche?.id_etat_final);
    const conf123FromFiche = () => {
      const conf1 = fiche.id_confirmateur ? getUserName(fiche.id_confirmateur) : '';
      const conf2 = fiche.id_confirmateur_2 ? getUserName(fiche.id_confirmateur_2) : '';
      const conf3 = fiche.id_confirmateur_3 ? getUserName(fiche.id_confirmateur_3) : '';
      const parts = [conf1, conf2, conf3].filter(Boolean);
      return parts.length > 0 ? parts.join(' | ') : '';
    };
    if (ETATS_DASHBOARD_CONF123.includes(etatId)) {
      const fromFiche = conf123FromFiche();
      if (fromFiche) return fromFiche;
      if (fiche.histo_confirmateurs_pseudo && String(fiche.histo_confirmateurs_pseudo).trim() !== '') {
        return fiche.histo_confirmateurs_pseudo;
      }
      return '';
    }
    if (fiche.histo_confirmateurs_pseudo && String(fiche.histo_confirmateurs_pseudo).trim() !== '') {
      return fiche.histo_confirmateurs_pseudo;
    }
    return conf123FromFiche();
  };

  // Confirmateur sélectionné : n'afficher que lui (sauf case « Inclure 2ème » → 1|2|3 avec surbrillance).
  // Évite RE/RP/backoffice « Mes actions » qui montrait tous les slots fiche alors qu'un seul est filtré.
  const renderConfirmateurCell = (fiche) => {
    const selectedId = appliedFilters.id_confirmateur ? String(appliedFilters.id_confirmateur) : null;
    const includeSecond = appliedFilters.include_confirmateur_2;

    if (selectedId && !includeSecond) {
      const name = getUserName(selectedId);
      return name || getConfirmateursFormatted(fiche);
    }

    if (includeSecond && selectedId) {
      const c1 = fiche.id_confirmateur ? getUserName(fiche.id_confirmateur) : null;
      const c2 = fiche.id_confirmateur_2 ? getUserName(fiche.id_confirmateur_2) : null;
      const c3 = fiche.id_confirmateur_3 ? getUserName(fiche.id_confirmateur_3) : null;
      const isSelected = (id) => id && String(id) === selectedId;
      const parts = [];
      if (c1) parts.push({ key: '1', id: fiche.id_confirmateur, name: c1 });
      if (c2) parts.push({ key: '2', id: fiche.id_confirmateur_2, name: c2 });
      if (c3) parts.push({ key: '3', id: fiche.id_confirmateur_3, name: c3 });
      if (parts.length === 0) return '';
      return (
        <>
          {parts.map((p, i) => (
            <span key={p.key}>
              {i > 0 && ' | '}
              <span className={isSelected(p.id) ? 'confirmateur-selected' : ''}>{p.name}</span>
            </span>
          ))}
        </>
      );
    }
    return getConfirmateursFormatted(fiche);
  };

  const fichesData = data?.data || [];
  const pagination = data?.pagination || { total: 0, page: 1, pages: 1 };
  
  // Masquer automatiquement le sidebar quand il y a des données dans le tableau (sur desktop uniquement)
  // IMPORTANT: Ce useEffect doit être appelé AVANT tous les early returns pour respecter les règles des hooks React
  useEffect(() => {
    if (isDesktop !== undefined && isDesktop) {
      // Admin: toujours garder le menu latéral visible
      if (Number(user?.fonction) === 1) {
        setAutoHide(false);
        return;
      }
      if (fichesData && fichesData.length > 0) {
        setAutoHide(true);
      } else {
        setAutoHide(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fichesData.length, isDesktop, user?.fonction]);

  const isLoadingList = isLoading && !data;
  const errorList = error;
  const refetchList = refetch;
  const isFetchingList = isLoading || isFetching || isSearching;
  const fichesQueryEnabled = !!appliedFilters.fiche_search || statsListOverride === 'upcoming';
  const [pageGenerationMs, setPageGenerationMs] = useState(null);
  const fetchStartRef = useRef(null);

  useEffect(() => {
    if (!fichesQueryEnabled) return undefined;

    if (isFetchingList) {
      if (fetchStartRef.current == null) {
        fetchStartRef.current = performance.now();
        setPageGenerationMs(null);
      }
    } else if (fetchStartRef.current != null) {
      setPageGenerationMs(performance.now() - fetchStartRef.current);
      fetchStartRef.current = null;
    }

    return undefined;
  }, [isFetchingList, fichesQueryEnabled]);

  if (errorList) {
    return (
      <div className="dashboard-error">
        <p>Erreur lors du chargement des fiches</p>
        <button onClick={() => refetchList()}>Réessayer</button>
      </div>
    );
  }

  // Mapping des colonnes aux clés de données
  const columnKeys = {
    'Nom': 'nom',
    'Prénom': 'prenom',
    'Téléphone': 'tel',
    'CP': 'cp',
    'Date Insertion': 'date_insert_time',
    'Date RDV': 'date_rdv_time',
    'État Final': 'id_etat_final',
    'Confirmateur': 'id_confirmateur',
    'Commercial': 'id_commercial',
    'Centre': 'id_centre',
    'Produit': 'produit',
    'Validé': 'valider',
  };

  // Fonction de tri
  const handleSort = (columnName) => {
    const key = columnKeys[columnName];
    if (!key) return;

    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setFilters((prev) => ({ ...prev, page: 1 }));
    setAppliedFilters((prev) => ({ ...prev, page: 1 }));
  };

  // Fonction pour obtenir l'icône de tri
  const getSortIcon = (columnName) => {
    const key = columnKeys[columnName];
    if (!key || sortConfig.key !== key) {
      return <FaSort className="sort-icon" />;
    }
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="sort-icon sort-active" />
      : <FaSortDown className="sort-icon sort-active" />;
  };

  // Fonction pour obtenir la valeur de tri pour une colonne
  const getSortValue = (fiche, key) => {
    // Pour les colonnes avec valeurs transformées, utiliser les fonctions helper
    if (key === 'id_confirmateur') {
      return getConfirmateursFormatted(fiche).toLowerCase();
    }
    if (key === 'id_commercial') {
      return getCommercialsFormatted(fiche).toLowerCase();
    }
    if (key === 'id_centre') {
      return getCentreName(fiche.id_centre).toLowerCase();
    }
    if (key === 'id_etat_final') {
      return getEtatDisplayName(fiche).toLowerCase();
    }
    if (key === 'produit') {
      return getProduitName(fiche.produit).toLowerCase();
    }
    
    // Pour les autres colonnes, utiliser la valeur brute
    let value = fiche[key];
    if (value == null) value = '';
    
    // Pour les dates, retourner le timestamp
    if (key.includes('date') || key.includes('time')) {
      return new Date(value || 0).getTime();
    }
    
    // Sinon, retourner la chaîne en minuscules
    return String(value).toLowerCase();
  };

  const sortedFiches = [...fichesData].sort((a, b) => {
    if (!sortConfig.key) return 0;

    const aValue = getSortValue(a, sortConfig.key);
    const bValue = getSortValue(b, sortConfig.key);

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    }

    const cmp = String(aValue ?? '').localeCompare(String(bValue ?? ''), 'fr', {
      numeric: true,
      sensitivity: 'base',
    });
    return sortConfig.direction === 'asc' ? cmp : -cmp;
  });

  const filteredFiches = debouncedQuickSearch.trim() === '' 
    ? sortedFiches 
    : sortedFiches.filter(fiche => {
        const searchLower = debouncedQuickSearch.trim().toLowerCase();
        // Rechercher dans tous les champs
        const searchFields = [
          fiche.nom || '',
          fiche.prenom || '',
          fiche.tel || '',
          fiche.cp || '',
          fiche.ville || '',
          fiche.adresse || '',
          formatDate(fiche.date_insert_time),
          formatRdvDateTime(fiche.date_rdv_time),
          formatDate(fiche.date_modif_time),
          getEtatDisplayName(fiche),
          getConfirmateursFormatted(fiche),
          getCommercialsFormatted(fiche),
          getCentreName(fiche.id_centre),
          getProduitName(fiche.produit),
          fiche.valider > 0 ? 'validé' : '',
          (fiche.rdv_urgent === 1 || fiche.rdv_urgent === true || fiche.qualification_code === 'RDV_URGENT') ? 'rdv urgent' : ''
        ];
        
        return searchFields.some(field => 
          field.toString().toLowerCase().includes(searchLower)
        );
      });

  const fiches = filteredFiches;
  const showDecalageColAdmin =
    useBadgeColumnsLayout && fiches.some((f) => ficheHasVisibleDecalageRequest(f));

  const handleExportResults = async (format) => {
    if (!canExportResults || exportingResults) return;
    if (!appliedFilters.fiche_search && statsListOverride !== 'upcoming') {
      toast.warning('Lancez d\'abord une recherche pour exporter les résultats');
      return;
    }
    setExportingResults(true);
    try {
      const params = { ...getQueryParams(), page: 1, limit: 999999 };
      const response = await api.get('/fiches', { params });
      const allFiches = response.data?.data || [];
      const exportColumns = getDashboardExportColumns({
        layout: useBadgeColumnsLayout ? 'badge' : 'standard',
        includeCq: showCqColumns,
        includeBadges: showBadgeColumns,
        includeDecalage: useBadgeColumnsLayout ? showDecalageColAdmin : true,
        isColVisible,
      });
      const exportData = buildDashboardExportRows(allFiches, {
        formatRdvDateTime,
        formatDate,
        etatsData: etatsData || [],
        getEtatDisplay: (f) => getEtatFullLabel(f) || getEtatDisplayName(f),
        getCommercial: (f) => {
          const v = getCommercialsFormatted(f);
          return v && v !== '-' ? v : '';
        },
        getConfirmateur: (f) => getConfirmateursFormatted(f),
        getCentre: (f) => getCentreName(f.id_centre),
        getProduit: (f) => getProduitName(f.produit),
        getIndicators: (f) => checkIndicators(f.id_etat_histo, f),
        getDecalage: (f) => {
          const info = getDecalageDashboardInfo(f);
          return info?.text || '';
        },
      });
      if (!exportData.length) {
        toast.warning('Aucune donnée à exporter');
        return;
      }
      if (!exportColumns.length) {
        toast.warning('Aucune colonne visible à exporter');
        return;
      }
      const filename = `dashboard_resultats_${new Date().toISOString().slice(0, 10)}`;
      if (format === 'csv') {
        exportToCSV(exportData, exportColumns, filename);
      } else {
        exportToExcel(exportData, exportColumns, filename);
      }
      toast.success(`${exportData.length} ligne(s) exportée(s)`);
    } catch (err) {
      console.error('Export Dashboard:', err);
      toast.error('Erreur lors de l\'export');
    } finally {
      setExportingResults(false);
    }
  };

  const copyFicheTelFromMenu = (tel) => {
    const t = (tel || '').trim();
    if (!t) {
      alert('Aucun numéro à copier');
      return;
    }
    navigator.clipboard.writeText(t).then(() => setFicheContextMenu(null)).catch(() => alert('Copie impossible'));
  };

  const openFicheDetailNewTab = (hash) => {
    if (!hash) return;
    window.open(`/fiches/${encodeURIComponent(hash)}?overlay=auto`, '_blank', 'noopener,noreferrer');
    setFicheContextMenu(null);
  };

  const openEtatModalFromMenu = () => {
    if (!ficheContextMenu?.fiche) return;
    const f = ficheContextMenu.fiche;
    setEtatModalFiche(f);
    setEtatModalNewId(f.id_etat_final != null ? String(f.id_etat_final) : '');
    setEtatModalMotif('');
    setEtatModalSousEtat(f.id_sous_etat != null ? String(f.id_sous_etat) : '');
    setFicheContextMenu(null);
  };

  const submitEtatModal = (ev) => {
    ev.preventDefault();
    if (!etatModalFiche || !etatModalNewId) return;
    const body = { id_etat_final: parseInt(etatModalNewId, 10) };
    if (etatModalMotif.trim()) body.motif_qualif = etatModalMotif.trim();
    const sousList = (sousEtatsData || []).filter((s) => Number(s.id_etat) === Number(etatModalNewId));
    if (sousList.length > 0) {
      if (!etatModalSousEtat) {
        alert('Veuillez sélectionner un sous-état.');
        return;
      }
      body.id_sous_etat = parseInt(etatModalSousEtat, 10);
    }
    updateEtatFromMenuMutation.mutate({ hash: etatModalFiche.hash, body });
  };

  const openAffectModalFromMenu = () => {
    if (!ficheContextMenu?.fiche) return;
    const f = ficheContextMenu.fiche;
    setAffectModalFiche(f);
    const cid = f.id_commercial != null && Number(f.id_commercial) > 0 ? String(f.id_commercial) : '';
    setAffectModalCommercialId(cid);
    setFicheContextMenu(null);
  };

  const submitAffectModal = (ev) => {
    ev.preventDefault();
    if (!affectModalFiche) return;
    const ficheId = resolveDashboardFicheNumericId(affectModalFiche);
    if (!ficheId) {
      alert('Identifiant fiche indisponible. Rechargez la page ou rouvrez la fiche.');
      return;
    }
    if (Number(affectModalFiche.id_etat_final) !== 7) {
      alert('L\'affectation n\'est possible que pour les fiches à l\'état « Confirmer » (confirmées).');
      return;
    }
    if (!affectModalCommercialId) {
      alert('Veuillez sélectionner un commercial.');
      return;
    }
    affectFromMenuMutation.mutate({
      fiches_ids: [ficheId],
      id_commercial: affectModalCommercialId,
    });
  };

  const handleDesaffectFromModal = () => {
    if (!affectModalFiche) return;
    const ficheId = resolveDashboardFicheNumericId(affectModalFiche);
    if (!ficheId) {
      alert('Identifiant fiche indisponible. Rechargez la page ou rouvrez la fiche.');
      return;
    }
    if (!window.confirm('Retirer l\'affectation commercial de cette fiche ?')) return;
    desaffectFromMenuMutation.mutate({ fiches_ids: [ficheId] });
  };

  const affectModalBusy =
    affectFromMenuMutation.isLoading || desaffectFromMenuMutation.isLoading;

  const openValidationModalFromMenu = () => {
    if (!ficheContextMenu?.fiche) return;
    const f = ficheContextMenu.fiche;
    setValidationModalFiche(f);
    setValidationConfRdvAvec(f.conf_rdv_avec != null && String(f.conf_rdv_avec).trim() !== '' ? String(f.conf_rdv_avec) : '');
    setValidationConfPresenceCouple(
      f.conf_presence_couple != null && String(f.conf_presence_couple).trim() !== ''
        ? String(f.conf_presence_couple)
        : ''
    );
    setFicheContextMenu(null);
  };

  const submitValidationModal = (ev) => {
    ev.preventDefault();
    if (!validationModalFiche?.hash) return;
    if (Number(validationModalFiche.id_etat_final) !== 7) {
      alert('Seules les fiches confirmées (état Confirmer) peuvent être validées.');
      return;
    }
    if (!String(validationConfRdvAvec || '').trim()) {
      alert('Veuillez sélectionner avec qui le RDV a été validé.');
      return;
    }
    if (!String(validationConfPresenceCouple || '').trim()) {
      alert('Veuillez sélectionner la présence du couple.');
      return;
    }
    validateFromMenuMutation.mutate({
      hash: validationModalFiche.hash,
      type_valid: `1${validationConfRdvAvec ? `-${validationConfRdvAvec}` : ''}`,
      conf_rdv_avec: validationConfRdvAvec || null,
      conf_presence_couple: validationConfPresenceCouple || null,
    });
  };

  const handleCancelValidationFromModal = () => {
    if (!validationModalFiche?.hash) return;
    if (!window.confirm('Voulez-vous annuler la validation de cette fiche ?')) return;
    validateFromMenuMutation.mutate({
      hash: validationModalFiche.hash,
      type_valid: '0',
    });
  };

  const runGeneratePdfFromMenu = (hash) => {
    if (!hash) return;
    setFicheContextMenu(null);
    generatePdfFromMenuMutation.mutate(hash);
  };

  const captureFicheRowFromMenu = async () => {
    const fiche = ficheContextMenu?.fiche;
    if (!fiche?.hash) return;
    setFicheContextMenu(null);
    try {
      await captureFicheTableRowScreenshot(fiche.hash, { fiche });
    } catch (err) {
      console.error(err);
      alert(err?.message || 'Capture d’écran impossible');
    }
  };

  const openFicheDetailFromMenu = () => {
    if (!ficheContextMenu?.fiche?.hash) return;
    openDashboardFicheDetail({ hash: ficheContextMenu.fiche.hash });
    setFicheContextMenu(null);
  };

  const openFicheHistoriqueOverlay = () => {
    if (!ficheContextMenu?.fiche?.hash) return;
    openDashboardFicheDetail({ hash: ficheContextMenu.fiche.hash, focusHistoriqueEtats: true });
    setFicheContextMenu(null);
  };

  const openFicheSmsFromMenu = () => {
    if (!ficheContextMenu?.fiche?.hash) return;
    openDashboardFicheDetail({ hash: ficheContextMenu.fiche.hash, initialTab: 'sms' });
    setFicheContextMenu(null);
  };

  const getFicheContextMenuStyle = () => {
    if (!ficheContextMenu || typeof window === 'undefined') {
      return { position: 'fixed', zIndex: 10050 };
    }

    const MENU_WIDTH = 220;
    const ITEM_HEIGHT = 38;
    const MENU_PADDING_Y = 8;
    const VIEWPORT_PADDING = 8;
    const itemCount = (hasPermission('fiche_validate') ? 9 : 8) + 1;
    const menuHeight = (itemCount * ITEM_HEIGHT) + MENU_PADDING_Y;
    const spaceBelow = window.innerHeight - ficheContextMenu.y;
    const spaceAbove = ficheContextMenu.y;
    const openUpward = spaceBelow < menuHeight + VIEWPORT_PADDING && spaceAbove > spaceBelow;

    const top = openUpward
      ? Math.max(VIEWPORT_PADDING, ficheContextMenu.y - menuHeight)
      : Math.min(
          ficheContextMenu.y,
          Math.max(VIEWPORT_PADDING, window.innerHeight - menuHeight - VIEWPORT_PADDING)
        );
    const left = Math.min(
      Math.max(VIEWPORT_PADDING, ficheContextMenu.x),
      Math.max(VIEWPORT_PADDING, window.innerWidth - MENU_WIDTH - VIEWPORT_PADDING)
    );

    return {
      position: 'fixed',
      left,
      top,
      zIndex: 10050,
      maxHeight: `calc(100vh - ${VIEWPORT_PADDING * 2}px)`,
      overflowY: 'auto',
    };
  };

  const validationModalBusy = validateFromMenuMutation.isLoading;

  return (
    <div className="dashboard">
      <SystemMessageBanner />
      <div className="dashboard-header">
        <div className="dashboard-header-left">
          {isDashboardTouchMobile && (
            <button
              type="button"
              className="btn-dashboard-view-toggle"
              onClick={toggleDashboardViewport}
              title={isTableDesktopView ? 'Revenir à la vue mobile' : 'Afficher le tableau en vue desktop'}
            >
              {isTableDesktopView ? (
                <>
                  <FaCompress /> Vue mobile
                </>
              ) : (
                <>
                  <FaExpand /> Vue tableau
                </>
              )}
            </button>
          )}
          {(user?.fonction === 1 || user?.fonction === 2 || user?.fonction === 7 || user?.fonction === 9) && (
            <button 
              className="btn-search-modal"
              onClick={() => setShowSearchModal(true)}
              title="Ouvrir la recherche avancée"
            >
              <FaSearch /> Recherche
            </button>
          )}
          <div>
            <h1><FaHome /> Tableau de bord</h1>
            <p>Bienvenue, {user?.pseudo || 'Utilisateur'}</p>
          </div>
        </div>
      </div>

      {/* Section des statistiques RDV - visible immédiatement */}
      {isLoadingStats ? (
        <div className="dashboard-stats-section">
          <div className="stats-cards">
            {[1, 2, 3].map((i) => (
              <div key={i} className="stat-card stat-card-skeleton">
                <div className="stat-card-icon"></div>
                <div className="stat-card-content">
                  <div className="stat-card-value stat-card-skeleton-pulse"></div>
                  <div className="stat-card-label stat-card-skeleton-pulse"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : dashboardStats && (() => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        const todayStr = `${year}-${month}-${day}`;
        
        // URL pour "confirmer de la journée" (fiches + fiches_histo id_etat=7, pas table confirmations)
        const confirmesUrl = `/dashboard?fiche_search=1&id_etat_final=7&date_champ=fiches_histo_confirmation&date_debut=${todayStr}&date_fin=${todayStr}&time_debut=00:00:00&time_fin=23:59:59`;
        
        const isConfirmateur = user?.fonction === 6;
        return (
          <div className="dashboard-stats-section">
            <div className="stats-cards">
              {/* Confirmer de la journée */}
              {isConfirmateur ? (
                <div className="stat-card stat-card-success">
                  <div className="stat-card-icon">
                    <FaCalendarCheck />
                  </div>
                  <div className="stat-card-content">
                    <div className="stat-card-value">{dashboardStats.rdvTodayConfirmed || 0}</div>
                    <div className="stat-card-label">Confirmer de la journée</div>
                  </div>
                </div>
              ) : (
                <Link to={confirmesUrl} className="stat-card stat-card-success">
                  <div className="stat-card-icon">
                    <FaCalendarCheck />
                  </div>
                  <div className="stat-card-content">
                    <div className="stat-card-value">{dashboardStats.rdvTodayConfirmed || 0}</div>
                    <div className="stat-card-label">Confirmer de la journée</div>
                  </div>
                </Link>
              )}

              {/* Signatures (aujourd'hui) : CR Signer pending + approved → page Compte rendu filtrée */}
              {isConfirmateur ? (
                <div className="stat-card stat-card-warning">
                  <div className="stat-card-icon">
                    <FaSignature />
                  </div>
                  <div className="stat-card-content">
                    <div className="stat-card-value">{dashboardStats.signaturesToday || 0}</div>
                    <div className="stat-card-label">Signatures</div>
                  </div>
                </div>
              ) : (
                <Link
                  to={(() => {
                    const d = new Date();
                    const yyyy = d.getFullYear();
                    const mm = String(d.getMonth() + 1).padStart(2, '0');
                    const dd = String(d.getDate()).padStart(2, '0');
                    return `/compte-rendu?signer=1&statut=all&date=${yyyy}-${mm}-${dd}`;
                  })()}
                  className="stat-card stat-card-warning"
                  title="Comptes rendus Signer du jour (approuvés et non approuvés)"
                >
                  <div className="stat-card-icon">
                    <FaSignature />
                  </div>
                  <div className="stat-card-content">
                    <div className="stat-card-value">{dashboardStats.signaturesToday || 0}</div>
                    <div className="stat-card-label">Signatures</div>
                  </div>
                </Link>
              )}

              {/* RDV à venir */}
              {isConfirmateur ? (
                <div className="stat-card stat-card-info">
                  <div className="stat-card-icon">
                    <FaCalendarAlt />
                  </div>
                  <div className="stat-card-content">
                    <div className="stat-card-value">{dashboardStats.rdvUpcoming || 0}</div>
                    <div className="stat-card-label">RDV à venir</div>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="stat-card stat-card-info stat-card-clickable"
                  onClick={() => setStatsListOverride('upcoming')}
                  title="Afficher les RDV à venir (sans modifier les filtres)"
                >
                  <div className="stat-card-icon">
                    <FaCalendarAlt />
                  </div>
                  <div className="stat-card-content">
                    <div className="stat-card-value">{dashboardStats.rdvUpcoming || 0}</div>
                    <div className="stat-card-label">RDV à venir</div>
                  </div>
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {/* Panneau de recherche et filtres */}
      <div className="search-panel">
        <div className="search-panel-header">
          <h2>
            <FaSearch /> Recherche et Filtres
          </h2>
        </div>

          <form className="search-form" onSubmit={handleSearch}>
            <div className="search-form-two-columns">
              {/* Colonne de gauche */}
              <div className="search-form-left">
                {/* Département */}
                {(user?.fonction !== 5 && user?.fonction !== 3) && (
                  <div className="form-group">
                    <label>Département(s) / code postal</label>
                    <input
                      type="text"
                      value={filters.cp || ''}
                      onChange={(e) => handleFilterChange('cp', e.target.value)}
                      placeholder="Ex. 75, 75001, 971 — plusieurs valeurs séparées par une virgule"
                    />
                  </div>
                )}

                {/* Critère de recherche */}
                <div className="form-group">
                  <label>Critère</label>
                  <input
                    type="text"
                    value={filters.critere || ''}
                    onChange={(e) => handleFilterChange('critere', e.target.value)}
                    placeholder="Critère"
                    required={user?.fonction === 5}
                  />
                </div>

                {/* État final */}
                <div className="form-group">
                  <label>État final</label>
                  {isLoadingEtats ? (
                    <select disabled>
                      <option>Chargement...</option>
                    </select>
                  ) : etatsError ? (
                    <select disabled>
                      <option>Erreur de chargement</option>
                    </select>
                  ) : (
                    <select
                      value={filters.id_etat_final || ''}
                      onChange={(e) => handleFilterChange('id_etat_final', e.target.value)}
                    >
                      <option value="">Tous</option>
                      {etatsPhase0.length > 0 && (
                        <optgroup label="PHASE 0">
                          {etatsPhase0.map(etat => (
                            <option key={etat.id} value={etat.id} style={{ backgroundColor: etat.color || '#cccccc' }}>
                              {etat.titre}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {etatsPhase1.length > 0 && (
                        <optgroup label="PHASE 1">
                          {etatsPhase1.map(etat => (
                            <option key={etat.id} value={etat.id} style={{ backgroundColor: etat.color || '#cccccc' }}>
                              {etat.titre}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {etatsPhase2.length > 0 && (
                        <optgroup label="PHASE 2">
                          {etatsPhase2.map(etat => (
                            <option key={etat.id} value={etat.id} style={{ backgroundColor: etat.color || '#cccccc' }}>
                              {etat.titre}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {etatsPhase3.length > 0 && (
                        <optgroup label="PHASE 3">
                          {user?.fonction !== 6 && (
                            <option value="t_s" style={{ backgroundColor: '#FF3380' }}>TOUT SIGNER</option>
                          )}
                          {etatsPhase3.map(etat => (
                            <option key={etat.id} value={etat.id} style={{ backgroundColor: etat.color || '#cccccc' }}>
                              {etat.titre}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {/* Si aucun état n'est trouvé dans les phases, afficher tous les états */}
                      {etatsPhase0.length === 0 && etatsPhase1.length === 0 && etatsPhase2.length === 0 && etatsPhase3.length === 0 && etats.length > 0 && (
                        <>
                          {etats.map(etat => (
                            <option key={etat.id} value={etat.id} style={{ backgroundColor: etat.color || '#cccccc' }}>
                              {etat.titre}
                            </option>
                          ))}
                        </>
                      )}
                    </select>
                  )}
                </div>

                {/* Sous-état (avant les champs date si l'état sélectionné en a) */}
                {showSousEtatFilter && (
                  <div className="form-group">
                    <label>Sous-état</label>
                    <select
                      value={filters.id_sous_etat || ''}
                      onChange={(e) => handleFilterChange('id_sous_etat', e.target.value)}
                    >
                      <option value="">Tout</option>
                      {sousEtatsForSelectedEtat.map(se => (
                        <option key={se.id} value={se.id}>{se.titre}</option>
                      ))}
                    </select>
                  </div>
                )}

                {showSourceFilter && (
                  <div className="form-group">
                    <label>Source</label>
                    <select
                      value={filters.annuler_repro_type || ''}
                      onChange={(e) => handleFilterChange('annuler_repro_type', e.target.value)}
                    >
                      <option value="">Tous</option>
                      <option value="compte_rendu">COMPTE RENDU</option>
                      <option value="repro_confirmateurs">REPRO CONFIRMATEURS</option>
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label>Champ de date</label>
                  <select
                    value={filters.date_champ || ''}
                    onChange={(e) => handleDateChampChange(e.target.value)}
                  >
                    <option value="">Sélectionnez date</option>
                    <option value="date_modif_time">Date Modification (état)</option>
                    <option value="date_insert_time">Date Insertion</option>
                    <option value="date_appel_time">Date d'appel</option>
                    {user?.fonction !== 3 && (
                      <option value="date_rdv_time">Date Planning</option>
                    )}
                    <option value="date_sign_time">Date signature</option>
                    <option value="fiches_histo">
                      {user?.fonction === 6
                        ? 'Mes actions sur la fiche (fiches_histo)'
                        : 'Changements d\'état du confirmateur (fiches_histo)'}
                    </option>
                    <option value="fiches_histo_confirmation">Date confirmation (fiches_histo)</option>
                  </select>
                </div>

                {/* Date début / fin: affichées uniquement si un champ de date est sélectionné */}
                {filters.date_champ && (
                  <>
                    <div className="form-group date-group">
                      <label>Date début</label>
                      <div className="date-time-inputs date-time-with-actuellement">
                        <input
                          type="datetime-local"
                          className="form-control"
                          step={60}
                          value={filterToDatetimeLocalValue(filters.date_debut, filters.time_debut, '00:00:00')}
                          onChange={(e) => handleDatetimeLocalChange('debut', e)}
                          aria-label="Date et heure début"
                        />
                        <button
                          type="button"
                          className="btn-datetime-actuellement"
                          onClick={() => applyNowToDatetimeBound('debut')}
                          title="Date et heure du moment"
                        >
                          Actuellement
                        </button>
                      </div>
                    </div>

                    <div className="form-group date-group">
                      <label>Date fin</label>
                      <div className="date-time-inputs date-time-with-actuellement">
                        <input
                          type="datetime-local"
                          className="form-control"
                          step={60}
                          value={filterToDatetimeLocalValue(filters.date_fin, filters.time_fin, '23:59:59')}
                          onChange={(e) => handleDatetimeLocalChange('fin', e)}
                          aria-label="Date et heure fin"
                        />
                        <button
                          type="button"
                          className="btn-datetime-actuellement"
                          onClick={() => applyNowToDatetimeBound('fin')}
                          title="Date et heure du moment"
                        >
                          Actuellement
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {/* Inclure archives */}
                <div className="form-group">
                  <label>Archives</label>
                  <label className="checkbox-inline">
                    <input
                      type="checkbox"
                      checked={!!filters.include_archive}
                      onChange={(e) => handleFilterChange('include_archive', e.target.checked)}
                    />
                  </label>
                </div>

              </div>

              {/* Colonne de droite */}
              <div className="search-form-right">
                {/* Produits */}
                {user?.fonction !== 5 && (
                  <div className="form-group">
                    <label>Produit</label>
                    <select
                      value={Array.isArray(filters.produit) ? filters.produit[0] || '' : filters.produit || ''}
                      onChange={(e) => handleFilterChange('produit', e.target.value ? e.target.value : '')}
                    >
                      <option value="">Tous les produits</option>
                      {produitsData && Array.isArray(produitsData) && produitsData.length > 0 ? (
                        produitsData.map(prod => (
                          <option key={prod.id} value={prod.id}>
                            {prod.nom || `Produit ${prod.id}`}
                          </option>
                        ))
                      ) : (
                        <>
                          <option value="1">PAC</option>
                          <option value="2">PV</option>
                        </>
                      )}
                    </select>
                  </div>
                )}

                {/* Type de critère (inclut Nom, Prénom, Téléphone, CP, Commentaire) */}
                <div className="form-group">
                  <label>Type de critère</label>
                  <select
                    value={filters.critere_champ || 'tel'}
                    onChange={(e) => handleFilterChange('critere_champ', e.target.value)}
                    required={user?.fonction === 5}
                  >
                    <option value="tel">Téléphone</option>
                    {user?.fonction !== 5 && (
                      <>
                        <option value="nom">Nom</option>
                        <option value="prenom">Prénom</option>
                        <option value="cp">Code Postal</option>
                        <option value="commentaire">Commentaire</option>
                      </>
                    )}
                  </select>
                </div>

                {/* Confirmateur (masqué pour confirmateur connecté : toujours lui) */}
                {user?.fonction !== 5 && user?.fonction !== 3 && user?.fonction !== 6 && (
                  <>
                    <div className="form-group">
                      <label>Confirmateur</label>
                      <select
                        value={filters.id_confirmateur || ''}
                        onChange={(e) => handleFilterChange('id_confirmateur', e.target.value)}
                      >
                        <option value="">Tous</option>
                        {confirmateurs.map(conf => (
                          <option key={conf.id} value={conf.id}>
                            {conf.pseudo}
                          </option>
                        ))}
                      </select>
                    </div>
                    {filters.id_confirmateur && (
                      <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          id="include_confirmateur_2"
                          checked={!!filters.include_confirmateur_2}
                          onChange={(e) => handleFilterChange('include_confirmateur_2', e.target.checked)}
                        />
                        <label htmlFor="include_confirmateur_2" style={{ marginBottom: 0 }}>Inclure 2ème confirmateur</label>
                      </div>
                    )}
                  </>
                )}

                {/* Centre */}
                {showCentreDashboardFilter && (
                  <div className="form-group">
                    <label>Centre</label>
                    <select
                      value={filters.id_centre || ''}
                      onChange={(e) => handleFilterChange('id_centre', e.target.value)}
                    >
                      <option value="">Tous</option>
                      {centres.map(centre => (
                        <option key={centre.id} value={centre.id}>
                          {centre.titre}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Commercial (aligné avec Date fin) */}
                {user?.fonction !== 5 && (
                  <div className="form-group">
                    <label>Commercial</label>
                    <select
                      value={filters.id_commercial || ''}
                      onChange={(e) => handleFilterChange('id_commercial', e.target.value)}
                    >
                      <option value="">Tous</option>
                      {commerciaux.map(com => (
                        <option key={com.id} value={com.id}>
                          {com.pseudo}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Confirmateur : inclure fiches en 2e / 3e slot — uniquement après recherche (param envoyé si coché) */}
                {user?.fonction === 6 && (
                  <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      id="include_confirmateur_2_session"
                      checked={!!filters.include_confirmateur_2}
                      onChange={(e) => handleFilterChange('include_confirmateur_2', e.target.checked)}
                    />
                    <label htmlFor="include_confirmateur_2_session" style={{ marginBottom: 0 }}>
                      Inclure 2ème confirmateur
                    </label>
                  </div>
                )}
              </div>

              <div className="search-form-actions-left">
                <button type="submit" className="btn-search">
                  <FaSearch /> RECHERCHE
                </button>
                <button type="button" onClick={handleReset} className="btn-reset">
                  Réinitialiser
                </button>
              </div>
            </div>
          </form>
      </div>

      {/* Résultats */}
      <div className="dashboard-results">
        {/* Zone de recherche rapide */}
        <div className="quick-search-container" style={{ marginBottom: '16px', position: 'relative' }}>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <FaSearch style={{ position: 'absolute', left: '12px', color: '#666', zIndex: 1 }} />
            <input
              type="text"
              className="quick-search-input"
              placeholder="Recherche rapide"
              value={quickSearch}
              onChange={(e) => setQuickSearch(e.target.value)}
              style={{ 
                width: '100%',
                padding: '10px 12px 10px 40px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
            {quickSearch && (
              <button
                onClick={() => setQuickSearch('')}
                style={{
                  position: 'absolute',
                  right: '8px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Effacer la recherche"
              >
                <FaTimes />
              </button>
            )}
          </div>
        </div>

        <div className="results-header">
          <div className="results-header-title-block">
            <h2>
              {debouncedQuickSearch.trim() !== '' 
                ? `Résultats de la recherche rapide: ${fiches.length} fiche${fiches.length > 1 ? 's' : ''}`
                : (appliedFilters.fiche_search || !!statsListOverride)
                  ? `Résultats de la recherche ${pagination.total}` 
                  : `${pagination.total}`}
            </h2>
            {canToggleBadges && !useBadgeColumnsLayout && fiches.length > 0 && (
              <button
                type="button"
                className="btn-toggle-fiche-badges"
                onClick={() => {
                  setShowFicheBadges((prev) => {
                    const next = !prev;
                    setFicheBadgesVisiblePref(next);
                    return next;
                  });
                }}
                title={showFicheBadges ? 'Cacher les colonnes badges (SIG, CS, R2, HAS, RF…)' : 'Afficher les colonnes badges (SIG, CS, R2, HAS, RF…)'}
              >
                {showFicheBadges ? <FaEyeSlash aria-hidden /> : <FaEye aria-hidden />}
                <span>{showFicheBadges ? 'Cacher les badges' : 'Afficher les badges'}</span>
              </button>
            )}
          </div>
          <div className="results-header-right">
            {isCommercialRdvView && (
              <div className="dashboard-commercial-day-select">
                <label htmlFor="dashboard-commercial-day">Jour</label>
                <select
                  id="dashboard-commercial-day"
                  className="dashboard-commercial-day-select-input"
                  value={commercialDayValue}
                  onChange={(e) => handleCommercialDayChange(e.target.value)}
                >
                  {commercialDayOptions.map((day) => (
                    <option key={day.dateStr} value={day.dateStr}>
                      {day.label}{day.isToday ? " (aujourd'hui)" : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {canExportResults && (
              <div className="export-buttons">
                <button
                  type="button"
                  className="btn-export"
                  title="Exporter en CSV"
                  onClick={() => handleExportResults('csv')}
                  disabled={exportingResults || isFetchingList || (!appliedFilters.fiche_search && statsListOverride !== 'upcoming')}
                >
                  <FaFileCsv /> {exportingResults ? 'Export...' : 'CSV'}
                </button>
                <button
                  type="button"
                  className="btn-export"
                  title="Exporter en Excel"
                  onClick={() => handleExportResults('excel')}
                  disabled={exportingResults || isFetchingList || (!appliedFilters.fiche_search && statsListOverride !== 'upcoming')}
                >
                  <FaFileExcel /> {exportingResults ? 'Export...' : 'Excel'}
                </button>
              </div>
            )}
            {canCustomizeColumns && fiches.length > 0 && (
              <FicheTableColumnsPicker
                layout={useBadgeColumnsLayout ? 'badge' : 'standard'}
                availableGroups={{
                  cq: showCqColumns,
                  decalage: useBadgeColumnsLayout ? showDecalageColAdmin : true,
                  badges: showBadgeColumns,
                }}
                visibility={columnVisibility}
                onChange={setColumnVisibility}
              />
            )}
            {(isFetchingList) && (
              <div className="search-loading-indicator">
                <div className="spinner-small"></div>
                <span>Recherche en cours...</span>
              </div>
            )}
          </div>
        </div>

        {(isLoading || isSearching || isFetching) && fiches.length === 0 ? (
          <div className="dashboard-loading">
            <div className="spinner"></div>
            <p>Chargement des résultats de recherche...</p>
            <div className="table-skeleton" style={{ marginTop: '24px', width: '100%' }}>
              {[...Array(5)].map((_, idx) => (
                <div key={idx} className="table-skeleton-row" style={{ marginBottom: '8px', height: '45px' }}></div>
              ))}
            </div>
          </div>
        ) : !isFetchingList && fiches.length === 0 ? (
          <div className="no-results">
            <p>Aucune fiche trouvée{debouncedQuickSearch ? ` pour "${debouncedQuickSearch}"` : ''}</p>
          </div>
        ) : (
          <>
            <div className={`fiches-table-container ${isFetchingList ? 'loading' : ''}`}>
<table className={`fiches-table${useBadgeColumnsLayout ? " fiches-table--admin-layout" : ""}`}>
                <thead>
                  <tr>
                    <FicheRowSelectHeader />
                    <FicheRowIndexHeader />
                    {isColVisible('nom') ? (
                      <th onClick={() => handleSort('Nom')} className="sortable-header fiche-col-nom">
                        Nom {getSortIcon('Nom')}
                      </th>
                    ) : null}
                    {isColVisible('prenom') ? (
                      <th onClick={() => handleSort('Prénom')} className="sortable-header fiche-col-prenom">
                        Prénom {getSortIcon('Prénom')}
                      </th>
                    ) : null}
                    {isColVisible('tel') ? (
                      <th onClick={() => handleSort('Téléphone')} className="sortable-header">
                        Téléphone {getSortIcon('Téléphone')}
                      </th>
                    ) : null}
                    {isColVisible('cp') ? (
                      <th onClick={() => handleSort('CP')} className="sortable-header">
                        CP {getSortIcon('CP')}
                      </th>
                    ) : null}
                    {useBadgeColumnsLayout ? (
                      <>
                        {isColVisible('date_rdv') ? (
                          <th onClick={() => handleSort('Date RDV')} className="sortable-header fiche-col-date">
                            Date RDV {getSortIcon('Date RDV')}
                          </th>
                        ) : null}
                        {isColVisible('commercial') ? (
                          <th onClick={() => handleSort('Commercial')} className="sortable-header">
                            Commercial {getSortIcon('Commercial')}
                          </th>
                        ) : null}
                        {showCqColumns && isColVisible('installateur') ? (
                          <th className="fiche-col-installateur">Installateur</th>
                        ) : null}
                        <FicheAdminBadgeHeaders
                          getSortIcon={getSortIcon}
                          onSortValide={() => handleSort('Validé')}
                          onSortProduit={() => handleSort('Produit')}
                          isColVisible={isColVisible}
                        />
                        {isColVisible('centre') ? (
                          <th onClick={() => handleSort('Centre')} className="sortable-header">
                            Centre {getSortIcon('Centre')}
                          </th>
                        ) : null}
                        {isColVisible('date_insert') ? (
                          <th onClick={() => handleSort('Date Insertion')} className="sortable-header fiche-col-date">
                            Date Insertion {getSortIcon('Date Insertion')}
                          </th>
                        ) : null}
                        {isColVisible('conf') ? (
                          <th onClick={() => handleSort('Confirmateur')} className="sortable-header">
                            Conf {getSortIcon('Confirmateur')}
                          </th>
                        ) : null}
                        {isColVisible('etat') ? (
                          <th onClick={() => handleSort('État Final')} className="sortable-header">
                            {isConfirmateurOrRE ? 'État actuel' : 'État Final'} {getSortIcon('État Final')}
                          </th>
                        ) : null}
                        {showCqColumns && isColVisible('cq_etat') ? (
                          <th className="fiche-col-cq">CQ État</th>
                        ) : null}
                        {showCqColumns && isColVisible('cq_dossier') ? (
                          <th className="fiche-col-cq">CQ Dossier</th>
                        ) : null}
                        {showCqColumns && isColVisible('cq_obs') ? (
                          <th className="fiche-col-cq-obs">Observation</th>
                        ) : null}
                        {showDecalageColAdmin && isColVisible('decalage') ? (
                          <th className="dashboard-decalage-col">Décalage</th>
                        ) : null}
                      </>
                    ) : (
                      <>
                        {isColVisible('date_insert') ? (
                          <th onClick={() => handleSort('Date Insertion')} className="sortable-header fiche-col-date">
                            Date Insertion {getSortIcon('Date Insertion')}
                          </th>
                        ) : null}
                        {isColVisible('date_rdv') ? (
                          <th onClick={() => handleSort('Date RDV')} className="sortable-header fiche-col-date">
                            Date RDV {getSortIcon('Date RDV')}
                          </th>
                        ) : null}
                        {isColVisible('etat') ? (
                          <th onClick={() => handleSort('État Final')} className="sortable-header">
                            {isConfirmateurOrRE ? 'État actuel' : 'État Final'} {getSortIcon('État Final')}
                          </th>
                        ) : null}
                        {showCqColumns && isColVisible('cq_etat') ? (
                          <th className="fiche-col-cq">CQ État</th>
                        ) : null}
                        {showCqColumns && isColVisible('cq_dossier') ? (
                          <th className="fiche-col-cq">CQ Dossier</th>
                        ) : null}
                        {showCqColumns && isColVisible('cq_obs') ? (
                          <th className="fiche-col-cq-obs">Observation</th>
                        ) : null}
                        {isColVisible('conf') ? (
                          <th onClick={() => handleSort('Confirmateur')} className="sortable-header">
                            Confirmateur {getSortIcon('Confirmateur')}
                          </th>
                        ) : null}
                        {isColVisible('commercial') ? (
                          <th onClick={() => handleSort('Commercial')} className="sortable-header">
                            Commercial {getSortIcon('Commercial')}
                          </th>
                        ) : null}
                        {showCqColumns && isColVisible('installateur') ? (
                          <th className="fiche-col-installateur">Installateur</th>
                        ) : null}
                        {isColVisible('centre') ? (
                          <th onClick={() => handleSort('Centre')} className="sortable-header">
                            Centre {getSortIcon('Centre')}
                          </th>
                        ) : null}
                        {isColVisible('prod') ? (
                          <th onClick={() => handleSort('Produit')} className="sortable-header">
                            Produit {getSortIcon('Produit')}
                          </th>
                        ) : null}
                        {isColVisible('valide') ? (
                          <th onClick={() => handleSort('Validé')} className="sortable-header">
                            Validé {getSortIcon('Validé')}
                          </th>
                        ) : null}
                        {showBadgeColumns ? (
                          <FicheAdminBadgeHeaders
                            badgesOnly
                            isColVisible={isColVisible}
                          />
                        ) : null}
                        {isColVisible('action') ? <th>Actions</th> : null}
                        {isColVisible('decalage') ? (
                          <th className="dashboard-decalage-col">Décalage</th>
                        ) : null}
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {fiches.map((fiche, rowIndex) => {
                    const indicators = checkIndicators(fiche.id_etat_histo, fiche);
                    const etatColor = getEtatColor(fiche.id_etat_final, fiche);
                    const produitColor = getProduitColor(fiche.produit);
                    const decalageInfo = useBadgeColumnsLayout
                      ? (showDecalageColAdmin ? getDecalageDisplayInfo(fiche) : null)
                      : getDecalageDashboardInfo(fiche);

                    return (
                      <tr
                        key={fiche.hash}
                        className={getFicheRowByEtatClassName(etatColor)}
                        data-fiche-hash={fiche.hash}
                        {...bindFicheRowContextMenu(fiche, { enabled: canFicheContextMenu })}
                        style={{
                          backgroundColor: etatColor,
                        }}
                        title={getTooltipComment(fiche) || undefined}
                      >
                        <FicheRowSelectCell />
                        <FicheRowIndexCell
                          number={getFicheRowNumber(rowIndex, pagination.page, appliedFilters.limit)}
                        />
                        {isColVisible('nom') ? (
                          <td data-label="" className="fiche-row-lead-cell fiche-col-nom">
                            <div className="fiche-row-lead-cell-inner">
                              <FicheTableMobileDetailButton
                                show={isDashboardTouchMobile}
                                onClick={() => openDashboardFicheDetail({ hash: fiche.hash })}
                                isLastViewed={lastViewedFicheHash === fiche.hash}
                              />
                              <span className="fiche-row-lead-label">{fiche.nom || ''}</span>
                            </div>
                          </td>
                        ) : null}
                        {isColVisible('prenom') ? (
                          <td data-label="Prénom:" className="fiche-col-prenom">{fiche.prenom || ''}</td>
                        ) : null}
                        {isColVisible('tel') ? (
                          <td data-label="Téléphone:">{fiche.tel || ''}</td>
                        ) : null}
                        {isColVisible('cp') ? (
                          <td data-label="CP:">{fiche.cp || ''}</td>
                        ) : null}
                        {useBadgeColumnsLayout ? (
                          <>
                            {isColVisible('date_rdv') ? (
                              <td data-label="Date RDV:" className="fiche-col-date" style={{ textAlign: 'left' }}>{formatRdvDateTime(fiche.date_rdv_time)}</td>
                            ) : null}
                            {isColVisible('commercial') ? (
                              <td data-label="Commercial:">{getCommercialsFormatted(fiche) || ''}</td>
                            ) : null}
                            {showCqColumns && isColVisible('installateur') ? (
                              <td data-label="Installateur:" className="fiche-col-installateur">
                                {fiche.installeur || ''}
                              </td>
                            ) : null}
                            <FicheAdminValideCell
                              valider={fiche.valider}
                              confRdvAvec={fiche.conf_rdv_avec}
                              hidden={!isColVisible('valide')}
                            />
                            <FicheAdminActionCell
                              onDetail={() => openDashboardFicheDetail({ hash: fiche.hash })}
                              isLastViewed={lastViewedFicheHash === fiche.hash}
                              hidden={!isColVisible('action')}
                              extras={
                                isQualiteConfirmation ? (
                                  <QualiteConfirmationAuditButton
                                    fiche={fiche}
                                    onClick={() => setAuditFiche(fiche)}
                                  />
                                ) : null
                              }
                            />
                            <FicheAdminIndicatorCells
                              indicators={indicators}
                              produitName={getProduitName(fiche.produit)}
                              produitColor={produitColor}
                              onPdf={() => runGeneratePdfFromMenu(fiche.hash)}
                              isColVisible={isColVisible}
                            />
                            {isColVisible('centre') ? (
                              <td data-label="Centre:" className="fiche-col-centre" title={getCentreName(fiche.id_centre) || undefined}>{getCentreDisplay(fiche.id_centre)}</td>
                            ) : null}
                            {isColVisible('date_insert') ? (
                              <td data-label="Date Insertion:" className="fiche-col-date" style={{ textAlign: 'left' }}>{formatDate(fiche.date_insert_time)}</td>
                            ) : null}
                            {isColVisible('conf') ? (
                              <td data-label="Confirmateur:">{renderConfirmateurCell(fiche)}</td>
                            ) : null}
                            {isColVisible('etat') ? (
                              <td
                                data-label={isConfirmateurOrRE ? 'État actuel:' : 'État:'}
                                className="etat-col-cell"
                              >
                                <span className="etat-text" title={getEtatFullLabel(fiche) || undefined}>
                                  {showCRPrefix(fiche) && <span className="etat-text-cr">&lt;CR&gt;</span>}
                                  {getEtatDisplayName(fiche)}
                                  {(fiche.rdv_urgent === 1 || fiche.rdv_urgent === true || fiche.qualification_code === 'RDV_URGENT') && (
                                    <span className="etat-text-urgent">(RDV_URGENT)</span>
                                  )}
                                </span>
                              </td>
                            ) : null}
                            {showCqColumns && isColVisible('cq_etat') ? (
                              <td data-label="CQ État:" className="fiche-col-cq">{fiche.cqe || ''}</td>
                            ) : null}
                            {showCqColumns && isColVisible('cq_dossier') ? (
                              <td data-label="CQ Dossier:" className="fiche-col-cq">{fiche.cqd || ''}</td>
                            ) : null}
                            {showCqColumns && isColVisible('cq_obs') ? (
                              <td data-label="Observation:" className="fiche-col-cq-obs" title={cleanObservationCQ(fiche.observations_cq) || undefined}>
                                {cleanObservationCQ(fiche.observations_cq) || ''}
                              </td>
                            ) : null}
                            {showDecalageColAdmin && isColVisible('decalage') ? (
                              <td data-label="Décalage:" className="dashboard-decalage-cell">
                                {decalageInfo ? (
                                  <span className={`dashboard-decalage-mention is-${decalageInfo.status}`}>
                                    {decalageInfo.text}
                                  </span>
                                ) : null}
                              </td>
                            ) : null}
                          </>
                        ) : (
                          <>
                            {isColVisible('date_insert') ? (
                              <td data-label="Date Insertion:" className="fiche-col-date" style={{ textAlign: 'left' }}>{formatDate(fiche.date_insert_time)}</td>
                            ) : null}
                            {isColVisible('date_rdv') ? (
                              <td data-label="Date RDV:" className="fiche-col-date" style={{ textAlign: 'left' }}>{formatRdvDateTime(fiche.date_rdv_time)}</td>
                            ) : null}
                            {isColVisible('etat') ? (
                              <td
                                data-label={isConfirmateurOrRE ? 'État actuel:' : 'État:'}
                                className="etat-col-cell"
                              >
                                <span className="etat-text" title={getEtatFullLabel(fiche) || undefined}>
                                  {showCRPrefix(fiche) && <span className="etat-text-cr">&lt;CR&gt;</span>}
                                  {getEtatDisplayName(fiche)}
                                  {(fiche.rdv_urgent === 1 || fiche.rdv_urgent === true || fiche.qualification_code === 'RDV_URGENT') && (
                                    <span className="etat-text-urgent">(RDV_URGENT)</span>
                                  )}
                                </span>
                              </td>
                            ) : null}
                            {showCqColumns && isColVisible('cq_etat') ? (
                              <td data-label="CQ État:" className="fiche-col-cq">{fiche.cqe || ''}</td>
                            ) : null}
                            {showCqColumns && isColVisible('cq_dossier') ? (
                              <td data-label="CQ Dossier:" className="fiche-col-cq">{fiche.cqd || ''}</td>
                            ) : null}
                            {showCqColumns && isColVisible('cq_obs') ? (
                              <td data-label="Observation:" className="fiche-col-cq-obs" title={cleanObservationCQ(fiche.observations_cq) || undefined}>
                                {cleanObservationCQ(fiche.observations_cq) || ''}
                              </td>
                            ) : null}
                            {isColVisible('conf') ? (
                              <td data-label="Confirmateur:">{renderConfirmateurCell(fiche)}</td>
                            ) : null}
                            {isColVisible('commercial') ? (
                              <td data-label="Commercial:">{getCommercialsFormatted(fiche) || ''}</td>
                            ) : null}
                            {showCqColumns && isColVisible('installateur') ? (
                              <td data-label="Installateur:" className="fiche-col-installateur">
                                {fiche.installeur || ''}
                              </td>
                            ) : null}
                            {isColVisible('centre') ? (
                              <td data-label="Centre:" className="fiche-col-centre" title={getCentreName(fiche.id_centre) || undefined}>{getCentreDisplay(fiche.id_centre)}</td>
                            ) : null}
                            {isColVisible('prod') ? (
                              <td data-label="Produit:">
                                <span
                                  className="produit-indicator"
                                  style={{ backgroundColor: produitColor, color: '#ffffff' }}
                                  title={getProduitName(fiche.produit)}
                                >
                                  {getProduitName(fiche.produit)}
                                </span>
                              </td>
                            ) : null}
                            {isColVisible('valide') ? (
                              <td data-label="Validé:" style={{ textAlign: 'center' }}>
                                {fiche.valider > 0 ? (
                                  <FaCheck
                                    style={{
                                      color: '#000000',
                                      fontSize: '15.3px',
                                      cursor: 'pointer'
                                    }}
                                    title={`Validée${fiche.conf_rdv_avec ? ` avec ${fiche.conf_rdv_avec}` : ''}`}
                                  />
                                ) : null}
                              </td>
                            ) : null}
                            {showBadgeColumns ? (
                              <FicheAdminIndicatorCells
                                badgesOnly
                                indicators={indicators}
                                isColVisible={isColVisible}
                              />
                            ) : null}
                            {isColVisible('action') ? (
                              <td data-label="">
                                {!canToggleBadges && (
                                <div className="fiche-indicators">
                                  {indicators.r2 && (
                                    <span className="indicator r2" title="R2 placé (commercial secondaire)">
                                      R2
                                    </span>
                                  )}
                                  {showHasBadge && indicators.has && (
                                    <span className="indicator has" title="Honoré à suivre">HAS</span>
                                  )}
                                  {indicators.rf && <span className="indicator rf" title="Refus">REF</span>}
                                  {indicators.sg && <span className="indicator sg" title="Signé">SIG</span>}
                                  {indicators.an && <span className="indicator an" title="Annulation">ANN</span>}
                                  {indicators.cs && <span className="indicator rs" title="SEUL">SEUL</span>}
                                </div>
                                )}
                                <div className="qualite-confirmation-actions-cell dashboard-actions-cell">
                                  {isQualiteConfirmation && (
                                    <QualiteConfirmationAuditButton
                                      fiche={fiche}
                                      onClick={() => setAuditFiche(fiche)}
                                    />
                                  )}
                                  <button
                                    onClick={() => openDashboardFicheDetail({ hash: fiche.hash })}
                                    className="btn-detail"
                                    title="Voir les détails"
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                                  >
                                    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <FaSearch style={{ color: '#ffffff', fontSize: '11.9px' }} />
                                      {lastViewedFicheHash === fiche.hash && (
                                        <span
                                          style={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            width: '28px',
                                            height: '28px',
                                            border: '3px solid #9e9e9e',
                                            borderRadius: '1px',
                                            backgroundColor: 'transparent',
                                            boxSizing: 'border-box'
                                          }}
                                        />
                                      )}
                                    </span>
                                  </button>
                                </div>
                              </td>
                            ) : null}
                            {isColVisible('decalage') ? (
                              <td data-label="Décalage:" className="dashboard-decalage-cell">
                                {decalageInfo && (
                                  <span className={`dashboard-decalage-mention is-${decalageInfo.status}`}>
                                    {decalageInfo.text}
                                  </span>
                                )}
                              </td>
                            ) : null}
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="pagination-container">
              <div className="limit-selector">
                <label htmlFor="limit-select">Afficher :</label>
                <select
                  id="limit-select"
                  value={filters.limit === 999999 ? 'all' : filters.limit}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleFilterChange('limit', value === 'all' ? 999999 : parseInt(value));
                    handleFilterChange('page', 1); // Reset à la page 1
                  }}
                  className="limit-select"
                >
                  <option value="10">10</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="200">200</option>
                  <option value="all">Tout</option>
                </select>
              </div>
              {pagination.pages > 1 && filters.limit !== 999999 && quickSearch.trim() === '' && (
                <div className="pagination">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                  >
                    Précédent
                  </button>
                  <span>
                    Page {pagination.page} sur {pagination.pages}
                  </span>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.pages}
                  >
                    Suivant
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {/* Tableau des confirmateurs avec leurs RDV - Affichage conditionnel selon la permission */}
        {/* Masquer pour Confirmateur (fonction 6) ; visible pour RE Confirmation (14) et les autres rôles autorisés */}
        {(hasPermission('dashboard_view_confirmateurs_tabs') || isREConfirmation || isRPConfirmation) &&
         !isConfirmateur &&
         !isLoadingStats && dashboardStats && (
          <div className="confirmateurs-table-section">
            <div className="confirmateurs-table-header">
              <h3 className="confirmateurs-table-title">Confirmateurs et leurs RDV</h3>
              <button 
                className="btn-toggle-confirmateurs"
                onClick={() => setShowConfirmateursTable(!showConfirmateursTable)}
                title={showConfirmateursTable ? 'Masquer le tableau' : 'Afficher le tableau'}
              >
                {showConfirmateursTable ? (
                  <>
                    <FaEyeSlash /> Masquer
                  </>
                ) : (
                  <>
                    <FaEye /> Afficher
                  </>
                )}
              </button>
            </div>
            {showConfirmateursTable && (
            <div className="confirmateurs-table-wrapper">
            {dashboardStats.confirmateurs && Array.isArray(dashboardStats.confirmateurs) && dashboardStats.confirmateurs.length > 0 ? (
              <div className="confirmateurs-table-container">
                <table className="confirmateurs-table">
                  <thead>
                    <tr>
                      <th>Confirmateur</th>
                      <th>RDV Aujourd'hui</th>
                      <th>RDV à Venir</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardStats.confirmateurs.map((conf) => (
                      <tr key={conf.id}>
                        <td>
                          <div className="confirmateur-cell">
                            {conf.photo ? (
                              <img src={conf.photo} alt={conf.pseudo} className="confirmateur-avatar-small" />
                            ) : (
                              <div className="confirmateur-avatar-small placeholder">
                                {conf.pseudo ? conf.pseudo.charAt(0).toUpperCase() : '?'}
                              </div>
                            )}
                            <span className="confirmateur-name">{conf.pseudo || 'N/A'}</span>
                          </div>
                        </td>
                        <td className="rdv-count-cell">
                          <span className={`rdv-count ${(conf.rdv_today === 0 || !conf.rdv_today) ? 'zero' : ''}`}>
                            {conf.rdv_today || 0}
                          </span>
                        </td>
                        <td className="rdv-count-cell">
                          <span className={`rdv-count ${(conf.rdv_upcoming === 0 || !conf.rdv_upcoming) ? 'zero' : ''}`}>
                            {conf.rdv_upcoming || 0}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="confirmateurs-table-total">
                      <td><strong>Total Confirmer de la journée</strong></td>
                      <td className="rdv-count-cell">
                        <span className="rdv-count">
                          <strong>{dashboardStats.rdvTodayConfirmed || 0}</strong>
                        </span>
                      </td>
                      <td className="rdv-count-cell">
                        <span className="rdv-count">
                          <strong>{dashboardStats.rdvUpcoming || 0}</strong>
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <p style={{ color: '#666', textAlign: 'center', padding: '20px', background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
                {statsError ? 'Erreur lors du chargement des confirmateurs' : 'Aucun confirmateur actif trouvé'}
              </p>
            )}
            </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de détail de fiche */}
      {ficheDetailModal && (
        <FicheDetailModal
          ficheHash={ficheDetailModal.hash}
          onClose={closeDashboardFicheDetail}
          options={{
            focusHistoriqueEtats: isPartenaire ? false : !!ficheDetailModal.focusHistoriqueEtats,
            initialTab: isPartenaire ? 'fiches' : (ficheDetailModal.initialTab || undefined),
            pinchZoom: isDashboardTouchMobile,
          }}
        />
      )}

      {/* Modal de recherche */}
      {showSearchModal && (
        <div className="search-modal-overlay" onClick={() => setShowSearchModal(false)}>
          <div className="search-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="search-modal-header">
              <h2>
                <FaSearch /> Recherche et Filtres
              </h2>
              <button 
                className="search-modal-close"
                onClick={() => setShowSearchModal(false)}
                title="Fermer"
              >
                <FaTimes />
              </button>
            </div>
            <form className="search-modal-form" onSubmit={(e) => {
              e.preventDefault();
              handleSearch(e);
              setShowSearchModal(false);
            }}>
              <div className="search-modal-fields">
                {/* Produits */}
                {user?.fonction !== 5 && (
                  <div className="form-group">
                    <label>Produit</label>
                    <select
                      value={Array.isArray(filters.produit) ? filters.produit[0] || '' : filters.produit || ''}
                      onChange={(e) => handleFilterChange('produit', e.target.value ? e.target.value : '')}
                    >
                      <option value="">Tous les produits</option>
                      {produitsData && Array.isArray(produitsData) && produitsData.length > 0 ? (
                        produitsData.map(prod => (
                          <option key={prod.id} value={prod.id}>
                            {prod.nom || `Produit ${prod.id}`}
                          </option>
                        ))
                      ) : (
                        <>
                          <option value="1">PAC</option>
                          <option value="2">PV</option>
                        </>
                      )}
                    </select>
                  </div>
                )}

                {/* Critère de recherche */}
                <div className="form-group">
                  <label>Critère</label>
                  <input
                    ref={searchModalCritereRef}
                    type="text"
                    value={filters.critere || ''}
                    onChange={(e) => handleFilterChange('critere', e.target.value)}
                    placeholder="Critère"
                    required={user?.fonction === 5}
                  />
                </div>

                {/* Type de critère (inclut Nom, Prénom, Téléphone, CP, Commentaire) */}
                <div className="form-group">
                  <label>Type de critère</label>
                  <select
                    value={filters.critere_champ || 'tel'}
                    onChange={(e) => handleFilterChange('critere_champ', e.target.value)}
                    required={user?.fonction === 5}
                  >
                    <option value="tel">Téléphone</option>
                    {user?.fonction !== 5 && (
                      <>
                        <option value="nom">Nom</option>
                        <option value="prenom">Prénom</option>
                        <option value="cp">Code Postal</option>
                        <option value="commentaire">Commentaire</option>
                      </>
                    )}
                  </select>
                </div>

                {/* Département */}
                {(user?.fonction !== 5 && user?.fonction !== 3) && (
                  <div className="form-group">
                    <label>Département(s) / code postal</label>
                    <input
                      type="text"
                      value={filters.cp || ''}
                      onChange={(e) => handleFilterChange('cp', e.target.value)}
                      placeholder="Ex. 75, 75001, 971 — plusieurs valeurs séparées par une virgule"
                    />
                  </div>
                )}

                {/* Confirmateur (masqué pour confirmateur connecté : toujours lui) */}
                {user?.fonction !== 5 && user?.fonction !== 3 && user?.fonction !== 6 && (
                  <>
                    <div className="form-group">
                      <label>Confirmateur</label>
                      <select
                        value={filters.id_confirmateur || ''}
                        onChange={(e) => handleFilterChange('id_confirmateur', e.target.value)}
                      >
                        <option value="">Tous</option>
                        {confirmateurs.map(conf => (
                          <option key={conf.id} value={conf.id}>
                            {conf.pseudo}
                          </option>
                        ))}
                      </select>
                    </div>
                    {filters.id_confirmateur && (
                      <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          id="include_confirmateur_2_mobile"
                          checked={!!filters.include_confirmateur_2}
                          onChange={(e) => handleFilterChange('include_confirmateur_2', e.target.checked)}
                        />
                        <label htmlFor="include_confirmateur_2_mobile" style={{ marginBottom: 0 }}>Inclure 2ème confirmateur</label>
                      </div>
                    )}
                  </>
                )}

                {/* Centre */}
                {showCentreDashboardFilter && (
                  <div className="form-group">
                    <label>Centre</label>
                    <select
                      value={filters.id_centre || ''}
                      onChange={(e) => handleFilterChange('id_centre', e.target.value)}
                    >
                      <option value="">Tous</option>
                      {centres.map(centre => (
                        <option key={centre.id} value={centre.id}>
                          {centre.titre}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* État final */}
                <div className="form-group">
                  <label>État final</label>
                  {isLoadingEtats ? (
                    <select disabled>
                      <option>Chargement...</option>
                    </select>
                  ) : etatsError ? (
                    <select disabled>
                      <option>Erreur de chargement</option>
                    </select>
                  ) : (
                    <select
                      value={filters.id_etat_final !== undefined && filters.id_etat_final !== null ? filters.id_etat_final : ''}
                      onChange={(e) => handleFilterChange('id_etat_final', e.target.value)}
                      defaultValue=""
                    >
                      <option value="">Tous</option>
                      {etatsPhase0.length > 0 && (
                        <optgroup label="PHASE 0">
                          {etatsPhase0.map(etat => (
                            <option key={etat.id} value={etat.id} style={{ backgroundColor: etat.color || '#cccccc' }}>
                              {etat.titre}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {etatsPhase1.length > 0 && (
                        <optgroup label="PHASE 1">
                          {etatsPhase1.map(etat => (
                            <option key={etat.id} value={etat.id} style={{ backgroundColor: etat.color || '#cccccc' }}>
                              {etat.titre}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {etatsPhase2.length > 0 && (
                        <optgroup label="PHASE 2">
                          {etatsPhase2.map(etat => (
                            <option key={etat.id} value={etat.id} style={{ backgroundColor: etat.color || '#cccccc' }}>
                              {etat.titre}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {etatsPhase3.length > 0 && (
                        <optgroup label="PHASE 3">
                          {user?.fonction !== 6 && (
                            <option value="t_s" style={{ backgroundColor: '#FF3380' }}>TOUT SIGNER</option>
                          )}
                          {etatsPhase3.map(etat => (
                            <option key={etat.id} value={etat.id} style={{ backgroundColor: etat.color || '#cccccc' }}>
                              {etat.titre}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {etatsPhase0.length === 0 && etatsPhase1.length === 0 && etatsPhase2.length === 0 && etatsPhase3.length === 0 && etats.length > 0 && (
                        <>
                          {etats.map(etat => (
                            <option key={etat.id} value={etat.id} style={{ backgroundColor: etat.color || '#cccccc' }}>
                              {etat.titre}
                            </option>
                          ))}
                        </>
                      )}
                    </select>
                  )}
                </div>

                {/* Sous-état (avant les champs date si l'état sélectionné en a) */}
                {showSousEtatFilter && (
                  <div className="form-group">
                    <label>Sous-état</label>
                    <select
                      value={filters.id_sous_etat || ''}
                      onChange={(e) => handleFilterChange('id_sous_etat', e.target.value)}
                    >
                      <option value="">Tout</option>
                      {sousEtatsForSelectedEtat.map(se => (
                        <option key={se.id} value={se.id}>{se.titre}</option>
                      ))}
                    </select>
                  </div>
                )}

                {showSourceFilter && (
                  <div className="form-group">
                    <label>Source</label>
                    <select
                      value={filters.annuler_repro_type || ''}
                      onChange={(e) => handleFilterChange('annuler_repro_type', e.target.value)}
                    >
                      <option value="">Tous</option>
                      <option value="compte_rendu">COMPTE RENDU</option>
                      <option value="repro_confirmateurs">REPRO CONFIRMATEURS</option>
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label>Champ de date</label>
                  <select
                    value={filters.date_champ || ''}
                    onChange={(e) => handleDateChampChange(e.target.value)}
                  >
                    <option value="">Sélectionnez date</option>
                    <option value="date_modif_time">Date Modification (état)</option>
                    <option value="date_insert_time">Date Insertion</option>
                    <option value="date_appel_time">Date d'appel</option>
                    {user?.fonction !== 3 && (
                      <option value="date_rdv_time">Date Planning</option>
                    )}
                    <option value="date_sign_time">Date signature</option>
                    <option value="fiches_histo">
                      {user?.fonction === 6
                        ? 'Mes actions sur la fiche (fiches_histo)'
                        : 'Changements d\'état du confirmateur (fiches_histo)'}
                    </option>
                    <option value="fiches_histo_confirmation">Date confirmation (fiches_histo)</option>
                  </select>
                </div>

                {user?.fonction === 6 && (
                  <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      id="include_confirmateur_2_session_mobile"
                      checked={!!filters.include_confirmateur_2}
                      onChange={(e) => handleFilterChange('include_confirmateur_2', e.target.checked)}
                    />
                    <label htmlFor="include_confirmateur_2_session_mobile" style={{ marginBottom: 0 }}>
                      Inclure 2ème confirmateur
                    </label>
                  </div>
                )}

                {/* Date début / fin: affichées uniquement si un champ de date est sélectionné */}
                {filters.date_champ && (
                  <div className="form-group">
                    <label>Date début</label>
                    <div className="date-time-inputs date-time-with-actuellement">
                      <input
                        type="datetime-local"
                        className="form-control"
                        step={60}
                        value={filterToDatetimeLocalValue(filters.date_debut, filters.time_debut, '00:00:00')}
                        onChange={(e) => handleDatetimeLocalChange('debut', e)}
                        aria-label="Date et heure début"
                      />
                      <button
                        type="button"
                        className="btn-datetime-actuellement"
                        onClick={() => applyNowToDatetimeBound('debut')}
                        title="Date et heure du moment"
                      >
                        Actuellement
                      </button>
                    </div>
                  </div>
                )}

                {/* Commercial (aligné avec Date fin) */}
                {user?.fonction !== 5 && (
                  <div className="form-group">
                    <label>Commercial</label>
                    <select
                      value={filters.id_commercial || ''}
                      onChange={(e) => handleFilterChange('id_commercial', e.target.value)}
                    >
                      <option value="">Tous</option>
                      {commerciaux.map(com => (
                        <option key={com.id} value={com.id}>
                          {com.pseudo}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {filters.date_champ && (
                  <div className="form-group">
                    <label>Date fin</label>
                    <div className="date-time-inputs date-time-with-actuellement">
                      <input
                        type="datetime-local"
                        className="form-control"
                        step={60}
                        value={filterToDatetimeLocalValue(filters.date_fin, filters.time_fin, '23:59:59')}
                        onChange={(e) => handleDatetimeLocalChange('fin', e)}
                        aria-label="Date et heure fin"
                      />
                      <button
                        type="button"
                        className="btn-datetime-actuellement"
                        onClick={() => applyNowToDatetimeBound('fin')}
                        title="Date et heure du moment"
                      >
                        Actuellement
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="search-modal-actions">
                <button type="submit" className="btn-search">
                  <FaSearch /> RECHERCHE
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    handleReset();
                    setShowSearchModal(false);
                  }} 
                  className="btn-reset"
                >
                  Réinitialiser
                </button>
                <button 
                  type="button" 
                  onClick={() => setShowSearchModal(false)} 
                  className="btn-cancel"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {ficheContextMenu && (
        <div
          className="dashboard-fiche-context-menu"
          style={getFicheContextMenuStyle()}
          role="menu"
        >
          <button
            type="button"
            className="dashboard-fiche-context-menu-item"
            onClick={openFicheDetailFromMenu}
          >
            Détails
          </button>
          <button
            type="button"
            className="dashboard-fiche-context-menu-item"
            onClick={() => copyFicheTelFromMenu(ficheContextMenu.fiche.tel)}
          >
            Copier le téléphone
          </button>
          <button type="button" className="dashboard-fiche-context-menu-item" onClick={openEtatModalFromMenu}>
            Changer l&apos;état…
          </button>
          <button type="button" className="dashboard-fiche-context-menu-item" onClick={openAffectModalFromMenu}>
            Affectation…
          </button>
          {hasPermission('fiche_validate') && (
            <button type="button" className="dashboard-fiche-context-menu-item" onClick={openValidationModalFromMenu}>
              Validation…
            </button>
          )}
          {isQualiteConfirmation && (
            <button
              type="button"
              className="dashboard-fiche-context-menu-item"
              onClick={() => {
                setAuditFiche(ficheContextMenu.fiche);
                setFicheContextMenu(null);
              }}
            >
              Audit…
            </button>
          )}
          <button
            type="button"
            className="dashboard-fiche-context-menu-item"
            onClick={() => runGeneratePdfFromMenu(ficheContextMenu.fiche.hash)}
            disabled={generatePdfFromMenuMutation.isLoading}
          >
            {generatePdfFromMenuMutation.isLoading ? 'Génération PDF…' : 'Impression PDF…'}
          </button>
          <button type="button" className="dashboard-fiche-context-menu-item" onClick={captureFicheRowFromMenu}>
            Capture écran
          </button>
          <button type="button" className="dashboard-fiche-context-menu-item" onClick={openFicheHistoriqueOverlay}>
            Voir historique (modal)…
          </button>
          <button type="button" className="dashboard-fiche-context-menu-item" onClick={openFicheSmsFromMenu}>
            Envoyer un SMS…
          </button>
          <button
            type="button"
            className="dashboard-fiche-context-menu-item"
            onClick={() => openFicheDetailNewTab(ficheContextMenu.fiche.hash)}
          >
            Ouvrir dans un nouvel onglet
          </button>
        </div>
      )}

      {etatModalFiche && (
        <div
          className="dashboard-etat-modal-overlay"
          onClick={() => !updateEtatFromMenuMutation.isLoading && setEtatModalFiche(null)}
        >
          <div className="dashboard-etat-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="dashboard-etat-modal-title">
            <h3 id="dashboard-etat-modal-title">Changer l&apos;état</h3>
            <p className="dashboard-etat-modal-fiche">
              {etatModalFiche.nom} {etatModalFiche.prenom} — {etatModalFiche.tel || '—'}
            </p>
            <form onSubmit={submitEtatModal}>
              <div className="dashboard-etat-modal-field">
                <label htmlFor="dashboard-etat-select">Nouvel état</label>
                <select
                  id="dashboard-etat-select"
                  value={etatModalNewId}
                  onChange={(e) => {
                    setEtatModalNewId(e.target.value);
                    setEtatModalSousEtat('');
                  }}
                  required
                  disabled={isLoadingEtats || !!etatsError}
                >
                  <option value="">— Sélectionner —</option>
                  {etatsPhase0.length > 0 && (
                    <optgroup label="PHASE 0">
                      {etatsPhase0.map((etat) => (
                        <option key={etat.id} value={etat.id} style={{ backgroundColor: etat.color || '#cccccc' }}>
                          {etat.titre}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {etatsPhase1.length > 0 && (
                    <optgroup label="PHASE 1">
                      {etatsPhase1.map((etat) => (
                        <option key={etat.id} value={etat.id} style={{ backgroundColor: etat.color || '#cccccc' }}>
                          {etat.titre}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {etatsPhase2.length > 0 && (
                    <optgroup label="PHASE 2">
                      {etatsPhase2.map((etat) => (
                        <option key={etat.id} value={etat.id} style={{ backgroundColor: etat.color || '#cccccc' }}>
                          {etat.titre}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {etatsPhase3.length > 0 && (
                    <optgroup label="PHASE 3">
                      {etatsPhase3.map((etat) => (
                        <option key={etat.id} value={etat.id} style={{ backgroundColor: etat.color || '#cccccc' }}>
                          {etat.titre}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {etatsPhase0.length === 0 &&
                    etatsPhase1.length === 0 &&
                    etatsPhase2.length === 0 &&
                    etatsPhase3.length === 0 &&
                    etats.length > 0 &&
                    etats.map((etat) => (
                      <option key={etat.id} value={etat.id} style={{ backgroundColor: etat.color || '#cccccc' }}>
                        {etat.titre}
                      </option>
                    ))}
                </select>
              </div>
              {sousEtatsForEtatModal.length > 0 && (
                <div className="dashboard-etat-modal-field">
                  <label htmlFor="dashboard-sous-etat-select">Sous-état</label>
                  <select
                    id="dashboard-sous-etat-select"
                    value={etatModalSousEtat}
                    onChange={(e) => setEtatModalSousEtat(e.target.value)}
                    required
                  >
                    <option value="">— Sélectionner —</option>
                    {sousEtatsForEtatModal.map((se) => (
                      <option key={se.id} value={se.id}>
                        {se.titre}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="dashboard-etat-modal-field">
                <label htmlFor="dashboard-etat-motif">Commentaire (optionnel)</label>
                <textarea
                  id="dashboard-etat-motif"
                  value={etatModalMotif}
                  onChange={(e) => setEtatModalMotif(e.target.value)}
                  rows={3}
                  placeholder="Motif du changement d'état"
                />
              </div>
              <div className="dashboard-etat-modal-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setEtatModalFiche(null)}
                  disabled={updateEtatFromMenuMutation.isLoading}
                >
                  Annuler
                </button>
                <button type="submit" className="btn-search" disabled={updateEtatFromMenuMutation.isLoading}>
                  {updateEtatFromMenuMutation.isLoading ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {affectModalFiche && (
        <div
          className="dashboard-etat-modal-overlay"
          onClick={() => !affectModalBusy && setAffectModalFiche(null)}
        >
          <div
            className="dashboard-etat-modal dashboard-affect-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="dashboard-affect-modal-title"
          >
            <h3 id="dashboard-affect-modal-title">Affectation commercial</h3>
            <p className="dashboard-etat-modal-fiche">
              {affectModalFiche.nom} {affectModalFiche.prenom} — {affectModalFiche.tel || '—'}
            </p>
            <p className="dashboard-affect-modal-hint">
              Commercial actuel :{' '}
              {affectModalFiche.id_commercial && Number(affectModalFiche.id_commercial) > 0
                ? affectModalFiche.commercial_pseudo || getUserName(affectModalFiche.id_commercial) || '—'
                : 'aucun'}
            </p>
            {Number(affectModalFiche.id_etat_final) !== 7 && (
              <p className="dashboard-affect-modal-warning">
                L&apos;affectation (assigner un commercial) n&apos;est autorisée par le serveur que pour les fiches à
                l&apos;état « Confirmer ». Vous pouvez toutefois désaffecter si un commercial est encore renseigné.
              </p>
            )}
            <form onSubmit={submitAffectModal}>
              <div className="dashboard-etat-modal-field">
                <label htmlFor="dashboard-affect-commercial">Commercial</label>
                <select
                  id="dashboard-affect-commercial"
                  value={affectModalCommercialId}
                  onChange={(e) => setAffectModalCommercialId(e.target.value)}
                  disabled={affectModalBusy || Number(affectModalFiche.id_etat_final) !== 7}
                >
                  <option value="">— Sélectionner —</option>
                  {commerciaux.map((com) => (
                    <option key={com.id} value={com.id}>
                      {com.pseudo}
                    </option>
                  ))}
                </select>
              </div>
              <div className="dashboard-etat-modal-actions dashboard-affect-modal-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setAffectModalFiche(null)}
                  disabled={affectModalBusy}
                >
                  Annuler
                </button>
                {affectModalFiche.id_commercial != null && Number(affectModalFiche.id_commercial) > 0 && (
                  <button
                    type="button"
                    className="btn-reset"
                    onClick={handleDesaffectFromModal}
                    disabled={affectModalBusy}
                  >
                    {desaffectFromMenuMutation.isLoading ? '…' : 'Désaffecter'}
                  </button>
                )}
                <button
                  type="submit"
                  className="btn-search"
                  disabled={
                    affectModalBusy ||
                    Number(affectModalFiche.id_etat_final) !== 7 ||
                    !affectModalCommercialId
                  }
                >
                  {affectFromMenuMutation.isLoading ? 'Enregistrement…' : 'Affecter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {validationModalFiche && (
        <div
          className="dashboard-etat-modal-overlay"
          onClick={() => !validationModalBusy && setValidationModalFiche(null)}
        >
          <div
            className="dashboard-etat-modal dashboard-validation-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="dashboard-validation-modal-title"
          >
            <h3 id="dashboard-validation-modal-title">Validation RDV</h3>
            <p className="dashboard-etat-modal-fiche">
              {validationModalFiche.nom} {validationModalFiche.prenom} — {validationModalFiche.tel || '—'}
            </p>
            {Number(validationModalFiche.id_etat_final) !== 7 && (
              <p className="dashboard-affect-modal-warning">
                La validation n&apos;est gérée par le serveur que pour les fiches à l&apos;état « Confirmer ».
              </p>
            )}
            {Number(validationModalFiche.valider) > 0 ? (
              <>
                <p className="dashboard-affect-modal-hint">Cette fiche est déjà validée.</p>
                <div className="dashboard-etat-modal-actions">
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={() => setValidationModalFiche(null)}
                    disabled={validationModalBusy}
                  >
                    Fermer
                  </button>
                  <button
                    type="button"
                    className="btn-search"
                    onClick={handleCancelValidationFromModal}
                    disabled={validationModalBusy || Number(validationModalFiche.id_etat_final) !== 7}
                  >
                    {validationModalBusy ? '…' : 'Annuler la validation'}
                  </button>
                </div>
              </>
            ) : (
              <form onSubmit={submitValidationModal}>
                <div className="dashboard-etat-modal-field">
                  <label htmlFor="dashboard-validation-avec">Avec qui le RDV a-t-il été validé ? *</label>
                  <select
                    id="dashboard-validation-avec"
                    required
                    value={validationConfRdvAvec}
                    onChange={(e) => setValidationConfRdvAvec(e.target.value)}
                    disabled={validationModalBusy || Number(validationModalFiche.id_etat_final) !== 7}
                  >
                    <option value="">Sélectionner…</option>
                    <option value="MR">Mr</option>
                    <option value="MME">Mme</option>
                    <option value="MR et MME">Mr et Mme</option>
                  </select>
                </div>
                <div className="dashboard-etat-modal-field">
                  <label htmlFor="dashboard-validation-presence">Présence du couple *</label>
                  <select
                    id="dashboard-validation-presence"
                    value={validationConfPresenceCouple}
                    onChange={(e) => setValidationConfPresenceCouple(e.target.value)}
                    disabled={validationModalBusy || Number(validationModalFiche.id_etat_final) !== 7}
                  >
                    <option value="">Sélectionner…</option>
                    <option value="RAS PRESENCE CLIENT(S)">RAS PRESENCE CLIENT(S)</option>
                    <option value="MME SEULE SANS MR">MME SEULE SANS MR</option>
                    <option value="MR SEUL SANS MME">MR SEUL SANS MME</option>
                  </select>
                </div>
                <div className="dashboard-etat-modal-actions">
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={() => setValidationModalFiche(null)}
                    disabled={validationModalBusy}
                  >
                    Fermer
                  </button>
                  <button
                    type="submit"
                    className="btn-search"
                    disabled={
                      validationModalBusy ||
                      Number(validationModalFiche.id_etat_final) !== 7 ||
                      !String(validationConfRdvAvec || '').trim() ||
                      !String(validationConfPresenceCouple || '').trim()
                    }
                  >
                    {validationModalBusy ? '…' : 'Valider la fiche'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {auditFiche && (
        <QualiteConfirmationAuditModal
          fiche={auditFiche}
          onClose={() => setAuditFiche(null)}
          invalidateQueryKeys={[['fiches', appliedFilters, activeTab, debouncedQuickSearch, statsListOverride], ['fiches']]}
        />
      )}

      <ScrollToTopButton />

      {fichesQueryEnabled && pageGenerationMs != null && !isFetchingList && (
        <p className="dashboard-page-generation-time" aria-live="polite">
          Page générée en{' '}
          {(pageGenerationMs / 1000).toLocaleString('fr-FR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}{' '}
          secondes
        </p>
      )}
    </div>
  );
};


export default Dashboard;
